import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import PendingOrdersPanel from "@/components/PendingOrdersPanel";
import CustomerOrdersPanel from "@/components/CustomerOrdersPanel";
import PurchaseOrdersPanel from "@/components/PurchaseOrdersPanel";
import GodownApprovalsModal from "@/components/GodownApprovalsModal";

import {
  Scan,
  Plus,
  Star,
  Minus,
  Trash2,
  ShoppingCart,
  Printer,
  Search,
  Package,
  CheckCircle2,
  X,
  Zap,
  ReceiptText,
  User,
  Phone,
  Building2,
  ChevronDown,
  ChevronUp,
  Tag,
  Banknote,
  Landmark,
  CreditCard,
  Smartphone,
  Split,
  PauseCircle,
  PlayCircle,
  Clock,
  FileText,
  Percent,
  IndianRupee,
  ChevronRight,
  Hash,
  PlusCircle,
  MinusCircle,
  BookOpen,
  AlertTriangle,
  Keyboard,
  Camera,
  FlipHorizontal,
  Wifi,
  WifiOff,
  Truck,
} from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Scanner } from "@yudiel/react-qr-scanner";
import { p } from "framer-motion/client";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Constants ──────────────────────────────────────────────────
const PAYMENT_MODES = ["cash", "card", "upi", "split", "bank_transfer"];
const PAYMENT_ICONS = {
  bank_transfer: Landmark,
  cash: Banknote,
  card: CreditCard,
  upi: Smartphone,
  split: Split,
  credit: BookOpen,
};

// ── Invoice HTML builders ──────────────────────────────────────
const buildNonGSTInvoice = (order, userName) => {
  if (!order) return "";
  const rows = order.items
    .map(
      (item) => `
      <tr>
        <td style="padding:5px 3px;border-bottom:1px dashed #ddd;">${item.product_name}</td>
        <td style="padding:5px 3px;border-bottom:1px dashed #ddd;text-align:center;">${item.quantity}</td>
        <td style="padding:5px 3px;border-bottom:1px dashed #ddd;text-align:right;">₹${item.price.toFixed(2)}</td>
        <td style="padding:5px 3px;border-bottom:1px dashed #ddd;text-align:right;font-weight:700;">₹${item.total.toFixed(2)}</td>
      </tr>`,
    )
    .join("");
  const d = new Date(order.created_at);
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
    <style>*{margin:0;padding:0;box-sizing:border-box;}
    body{font-family:'Courier New',monospace;font-size:12px;width:100%;max-width:80mm;margin:0 auto;padding:3mm;}
    .center{text-align:center;}.bold{font-weight:700;}
    table{width:100%;border-collapse:collapse;}
    th{font-size:10px;font-weight:700;border-bottom:2px solid #000;padding:3px;}
    th:not(:first-child){text-align:right;}
    @media print{body{width:80mm;padding:3mm;}@page{margin:0;size:80mm auto;}}</style></head>
    <body>
    <div class="center" style="padding-bottom:10px;border-bottom:2px dashed #000;margin-bottom:10px;">
      <div style="font-size:18px;font-weight:900;letter-spacing:2px;">MRB HOME DECOR</div>
      <div style="font-size:10px;color:#555;margin-top:2px;">Home Decor Mall</div>
      <div style="font-size:10px;margin-top:3px;font-weight:700;">RECEIPT (Non-Tax)</div>
    </div>
    <div style="margin-bottom:10px;line-height:1.9;font-size:11px;">
      <div style="display:flex;justify-content:space-between;"><b>Receipt#</b><span>${order.invoice_number}</span></div>
      <div style="display:flex;justify-content:space-between;"><b>Date</b><span>${d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span></div>
      <div style="display:flex;justify-content:space-between;"><b>Time</b><span>${d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span></div>
      <div style="display:flex;justify-content:space-between;"><b>Cashier</b><span>${userName || "Staff"}</span></div>
      ${order.customer_name ? `<div style="display:flex;justify-content:space-between;"><b>Customer</b><span>${order.customer_name}</span></div>` : ""}
      
      ${order.customer_phone ? `<div style="display:flex;justify-content:space-between;"><b>Phone</b><span>${order.customer_phone}</span></div>` : ""}
      ${order.referred_by_staff_name ? `<div style="display:flex;justify-content:space-between;"><b>Ref. By</b><span>${order.referred_by_staff_name}</span></div>` : ""}
      <div style="display:flex;justify-content:space-between;"><b>Payment</b><span style="text-transform:uppercase;">${order.credit_card !== 0 ? "Card" : order.credit_cash !== 0 ? "Cash" : order.credit_upi !== 0 && "UPI"}</span></div>
    </div>
    <div style="border-top:2px dashed #000;border-bottom:2px dashed #000;padding:8px 0;margin-bottom:8px;">
    <table><thead><tr><th style="text-align:left;">ITEM</th><th>QTY</th><th>RATE</th><th>AMT</th></tr></thead>
    <tbody>${rows}</tbody></table></div>
    <div style="line-height:2;font-size:12px;">
      <div style="display:flex;justify-content:space-between;"><span>Subtotal</span><span>₹${order.subtotal.toFixed(2)}</span></div>
      ${order.discount > 0 ? `<div style="display:flex;justify-content:space-between;"><span>Discount</span><span>-₹${order.discount.toFixed(2)}</span></div>` : ""}
      ${order.round_off && order.round_off !== 0 ? `<div style="display:flex;justify-content:space-between;font-size:10px;color:#666;"><span>Round Off</span><span>${order.round_off > 0 ? "+" : ""}₹${order.round_off.toFixed(2)}</span></div>` : ""}
      <div style="display:flex;justify-content:space-between;font-size:15px;font-weight:900;border-top:2px solid #000;padding-top:5px;margin-top:5px;">
        <span>TOTAL</span><span>₹${order.total.toFixed(2)}</span></div>
    </div>
    ${order.narration ? `<div style="margin-top:8px;font-size:10px;color:#666;font-style:italic;">${order.narration}</div>` : ""}
    <div style="text-align:center;margin-top:14px;font-size:10px;color:#666;border-top:2px dashed #000;padding-top:10px;">
      <strong style="font-size:12px;display:block;margin-bottom:2px;">Thank you!</strong>
      Please visit again · Items sold are non-refundable without receipt
    </div></body></html>`;
};

const COMPANY_SETTINGS = {
  name: "M. R. B. SAnitary & Ceramic",
  address:
    "GF, Shed NO. 4,5,6 Survey No. 1525/83, TP.54, FP No.29, Jay Ambe Estate, Ognaj Circle, Opp. Purohit Hotel, Ahmedabad",
  phone: "9737012222",
  gstin: "24AUAPJ3156A1ZC",
  udyog_reg: "UDYAM-GJ-01-0193953 (Micro)",
  state: "Gujarat, Code : 24",
  email: "mrbceramic406@gmail.com",
  pan: "AUAPJ3156A",
  bankName: "ICICI BANK",
  bankAccount: "182805500120",
  bankIFSC: "ICIC0001828",
  bankBranch: "Chandlodiya, Ahmedabad",
};

const numberToWords = (amount) => {
  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const inWords = (n) => {
    if (n === 0) return "";
    if (n < 20) return ones[n] + " ";
    if (n < 100)
      return (
        tens[Math.floor(n / 10)] +
        (ones[n % 10] ? " " + ones[n % 10] : "") +
        " "
      );
    if (n < 1000)
      return ones[Math.floor(n / 100)] + " Hundred " + inWords(n % 100);
    if (n < 100000)
      return inWords(Math.floor(n / 1000)) + "Thousand " + inWords(n % 1000);
    if (n < 10000000)
      return inWords(Math.floor(n / 100000)) + "Lakh " + inWords(n % 100000);
    return inWords(Math.floor(n / 10000000)) + "Crore " + inWords(n % 10000000);
  };
  const rounded = Math.round(amount * 100) / 100;
  const intPart = Math.floor(rounded);
  const decPart = Math.round((rounded - intPart) * 100);
  let words =
    "Indian Rupees " + (intPart === 0 ? "Zero" : inWords(intPart).trim());
  if (decPart > 0) words += " and " + inWords(decPart).trim() + " Paise";
  return words + " Only";
};

const thermalPaymentData = (order) => {
  const methods = [
    ["Cash", Number(order?.credit_cash || 0)],
    ["Card", Number(order?.credit_card || 0)],
    ["UPI", Number(order?.credit_upi || 0)],
  ].filter(([, amount]) => amount > 0);
  return {
    label: methods.length > 1 ? "Split" : methods[0]?.[0] || order?.payment_mode || "Pending",
    methods,
  };
};

const buildThermalGSTInvoice = (order, userName) => {
  if (!order) return "";
  const money = (value) => `₹${Number(value || 0).toFixed(2)}`;
  const safeItems = order.items || [];
  const isIGST = order.gst_type === "igst";
  const payment = thermalPaymentData(order);
  const date = new Date(order.created_at);
  const dateText = date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  const timeText = date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  const items = safeItems.map((item) => {
    const finalRate = Number(item.price || 0);
    const quantity = Number(item.quantity || 0);
    const discount = Number(item.discount || 0);
    const originalRate = item.discount_type === "percentage" && discount > 0 && discount < 100
      ? finalRate / (1 - discount / 100)
      : finalRate + (item.discount_type === "amount" ? discount : 0);
    const saved = Math.max(0, (originalRate - finalRate) * quantity);
    const taxable = Number(item.taxable ?? (item.total || finalRate * quantity));
    const gstRate = Number(item.gst_rate || 0);
    const gstAmount = Number(item.gst_amount ?? ((Number(item.cgst || 0) + Number(item.sgst || 0) + Number(item.igst || 0)) || taxable * gstRate / 100));
    return { ...item, finalRate, quantity, discount, originalRate, saved, taxable, gstRate, gstAmount };
  });
  const itemSaved = items.reduce((sum, item) => sum + item.saved, 0);
  const tax = Number(order.total_tax ?? order.tax ?? 0);
  const cgst = Number(order.cgst || 0);
  const sgst = Number(order.sgst || 0);
  const igst = Number(order.igst || 0);
  const rows = items.map((item, index) => `
    <div class="item">
      <div class="item-title"><span>${index + 1}. ${item.product_name || "Item"}</span><b>${money(item.total)}</b></div>
      <div class="muted">${item.quantity} ${item.unit || "Nos"} × ${money(item.originalRate)}${item.discount > 0 ? ` &nbsp; Disc ${item.discount_type === "percentage" ? `${item.discount}%` : money(item.discount)}` : ""}</div>
      ${item.saved > 0 ? `<div class="saved">Saved ${money(item.saved)}</div>` : ""}
      <div class="muted">Taxable ${money(item.taxable)} · GST ${item.gstRate}% = ${money(item.gstAmount)}</div>
    </div>`).join("");
  const splitRows = payment.methods.map(([name, amount]) => `<div class="line"><span>${name}</span><span>${money(amount)}</span></div>`).join("");
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>Tax Invoice - ${order.invoice_number || ""}</title><style>
    *{box-sizing:border-box}html,body{margin:0;padding:0}body{width:100%;max-width:80mm;margin:0 auto;padding:3mm;font-family:Arial,sans-serif;font-size:10px;color:#000;overflow-wrap:anywhere}.center{text-align:center}.line,.item-title{display:flex;justify-content:space-between;gap:8px}.line{line-height:1.65}.muted{font-size:9px;color:#444;line-height:1.45}.saved{font-size:9px;color:#087f23}.rule{border-top:1px dashed #000;margin:7px 0}.item{padding:5px 0;border-bottom:1px dashed #999}.item-title{font-size:10px;font-weight:700}.grand{font-size:15px;font-weight:800;border-top:2px solid #000;padding-top:5px;margin-top:4px}.small{font-size:9px;color:#555;line-height:1.35}@media print{@page{size:80mm auto;margin:0}body{width:80mm;padding:3mm}}
  </style></head><body>
    <div class="center"><b style="font-size:15px">${COMPANY_SETTINGS.name}</b><div class="small">${COMPANY_SETTINGS.address}</div><div>GSTIN: ${COMPANY_SETTINGS.gstin}</div><b>TAX INVOICE</b></div>
    <div class="rule"></div><div class="line"><span>Invoice</span><b>${order.invoice_number || "-"}</b></div><div class="line"><span>Date</span><span>${dateText} ${timeText}</span></div><div class="line"><span>Payment</span><b>${payment.label}</b></div><div class="line"><span>Cashier</span><span>${userName || "Staff"}</span></div>
    ${order.customer_name ? `<div class="line"><span>Customer</span><span>${order.customer_name}</span></div>` : ""}${order.customer_phone ? `<div class="line"><span>Phone</span><span>${order.customer_phone}</span></div>` : ""}${order.referred_by_staff_name ? `<div class="line"><span>Staff Ref.</span><span>${order.referred_by_staff_name}</span></div>` : ""}
    <div class="rule"></div>${rows}<div class="rule"></div>
    <div class="line"><span>Subtotal</span><span>${money(order.subtotal)}</span></div>
    ${Number(order.discount || 0) > 0 ? `<div class="line"><span>Discount</span><span>- ${money(order.discount)}</span></div>` : ""}${Number(order.extra_discount || 0) > 0 ? `<div class="line"><span>Extra Discount</span><span>- ${money(order.extra_discount)}</span></div>` : ""}
    <div class="line"><span>Taxable Value</span><span>${money(items.reduce((s, i) => s + i.taxable, 0))}</span></div>${isIGST ? `<div class="line"><span>IGST</span><span>${money(igst || tax)}</span></div>` : `<div class="line"><span>CGST</span><span>${money(cgst)}</span></div><div class="line"><span>SGST</span><span>${money(sgst)}</span></div>`}
    ${Number(order.round_off || 0) !== 0 ? `<div class="line"><span>Round Off</span><span>${order.round_off > 0 ? "+" : "-"} ${money(Math.abs(order.round_off))}</span></div>` : ""}<div class="line grand"><span>GRAND TOTAL</span><span>${money(order.total)}</span></div>
    <div class="small" style="margin-top:4px">${numberToWords(order.total || 0)}</div>${itemSaved > 0 ? `<div class="saved" style="margin-top:5px"><b>You saved on items: ${money(itemSaved)}</b></div>` : ""}
    <div class="rule"></div><b>Payment Breakdown</b>${splitRows || `<div class="line"><span>${payment.label}</span><span>${money(order.amount_received || order.total)}</span></div>`}${order.narration ? `<div class="small" style="margin-top:5px">Note: ${order.narration}</div>` : ""}<div class="center small" style="margin-top:10px">Thank you! Please visit again.<br/>Computer generated invoice</div>
  </body></html>`;
};

const buildGSTInvoice = (order, userName) => {
  if (!order) return "";
  const isIGST = order.gst_type === "igst";
  const d = new Date(order.created_at);
  const dateStr = d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const timeStr = d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const discFactor =
    (order.subtotal || 0) > 0
      ? ((order.subtotal || 0) - (order.discount || 0)) / (order.subtotal || 1)
      : 1;
  const itemsData = (order.items || []).map((item) => {
    const rate = item.gst_rate != null ? item.gst_rate : 0;
    const inclusiveAmt = item.total || 0;
    const itemTaxable = inclusiveAmt;
    const gstAmt = rate > 0 ? (itemTaxable * rate) / 100 : 0;
    const cgst = isIGST ? 0 : gstAmt / 2;
    const sgst = isIGST ? 0 : gstAmt / 2;
    const igst = isIGST ? gstAmt : 0;
    return { ...item, rate, itemTaxable, gstAmt, cgst, sgst, igst };
  });
  const itemRowsHTML = itemsData
    .map((item, idx) => {
      const discPct =
        order.discount > 0 ? ((1 - discFactor) * 100).toFixed(3) + "%" : "";
      let gstLines = "";
      if (item.rate > 0) {
        if (isIGST) {
          gstLines = `<tr><td colspan="6" style="text-align:right;border-right:1px solid #aaa;padding:2px 5px;font-size:10px;color:#333;">IGST @ ${item.rate}%</td><td style="text-align:right;padding:2px 5px;font-size:10px;">${item.igst.toFixed(2)}</td></tr>`;
        } else {
          gstLines = `<tr><td colspan="6" style="text-align:right;border-right:1px solid #aaa;padding:2px 5px;font-size:10px;color:#333;">CGST @ ${item.rate / 2}%</td><td style="text-align:right;padding:2px 5px;font-size:10px;">${item.cgst.toFixed(2)}</td></tr><tr><td colspan="6" style="text-align:right;border-right:1px solid #aaa;padding:2px 5px;font-size:10px;color:#333;">SGST @ ${item.rate / 2}%</td><td style="text-align:right;padding:2px 5px;font-size:10px;">${item.sgst.toFixed(2)}</td></tr>`;
        }
      }
      return `<tr style="border-bottom:1px solid #ccc;"><td style="text-align:center;vertical-align:top;">${idx + 1}</td><td style="vertical-align:top;">${item.product_name}${item.hsn ? `<br/><span style="font-size:9px;color:#555;">HSN: ${item.hsn}</span>` : ""}</td><td style="text-align:center;vertical-align:top;">${item.hsn || ""}</td><td style="text-align:center;vertical-align:top;">${item.quantity} Nos</td><td style="text-align:right;vertical-align:top;">${item.rate > 0 ? (((item.price || 0) * 100) / (100 + item.rate)).toFixed(2) : (item.price || 0).toFixed(2)}</td><td style="text-align:center;vertical-align:top;">Nos</td><td style="text-align:right;vertical-align:top;border-right:1px solid #aaa;">${discPct}</td><td style="text-align:right;vertical-align:top;">${item.itemTaxable.toFixed(2)}</td></tr>${gstLines}`;
    })
    .join("");
  const hsnMap = {};
  itemsData.forEach((item) => {
    const key = item.hsn || "N/A";
    if (!hsnMap[key])
      hsnMap[key] = { taxable: 0, cgst: 0, sgst: 0, igst: 0, rate: item.rate };
    hsnMap[key].taxable += item.itemTaxable;
    hsnMap[key].cgst += item.cgst;
    hsnMap[key].sgst += item.sgst;
    hsnMap[key].igst += item.igst;
  });
  const hsnTotalTaxable = Object.values(hsnMap).reduce(
    (s, v) => s + v.taxable,
    0,
  );
  const hsnTotalCGST = Object.values(hsnMap).reduce((s, v) => s + v.cgst, 0);
  const hsnTotalSGST = Object.values(hsnMap).reduce((s, v) => s + v.sgst, 0);
  const hsnTotalIGST = Object.values(hsnMap).reduce((s, v) => s + v.igst, 0);
  const hsnTotalTax = hsnTotalCGST + hsnTotalSGST + hsnTotalIGST;
  const hsnTableHTML = isIGST
    ? `<table style="width:100%;border-collapse:collapse;font-size:10px;margin-top:-1px;"><thead><tr style="background:#f4f4f4;"><th style="border:1px solid #000;padding:3px 5px;text-align:left;">HSN/SAC</th><th style="border:1px solid #000;padding:3px 5px;text-align:right;">Taxable Value</th><th style="border:1px solid #000;padding:3px 5px;text-align:center;">IGST Rate</th><th style="border:1px solid #000;padding:3px 5px;text-align:right;">IGST Amount</th><th style="border:1px solid #000;padding:3px 5px;text-align:right;">Total Tax Amount</th></tr></thead><tbody>${Object.entries(
        hsnMap,
      )
        .map(
          ([hsn, data]) =>
            `<tr><td style="border:1px solid #000;padding:3px 5px;">${hsn}</td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${data.taxable.toFixed(2)}</td><td style="border:1px solid #000;padding:3px 5px;text-align:center;">${data.rate}%</td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${data.igst.toFixed(2)}</td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${data.igst.toFixed(2)}</td></tr>`,
        )
        .join(
          "",
        )}<tr style="font-weight:bold;"><td style="border:1px solid #000;padding:3px 5px;">Total</td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${hsnTotalTaxable.toFixed(2)}</td><td style="border:1px solid #000;padding:3px 5px;"></td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${hsnTotalIGST.toFixed(2)}</td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${hsnTotalTax.toFixed(2)}</td></tr></tbody></table>`
    : `<table style="width:100%;border-collapse:collapse;font-size:10px;margin-top:-1px;"><thead><tr style="background:#f4f4f4;"><th style="border:1px solid #000;padding:3px 5px;text-align:left;">HSN/SAC</th><th style="border:1px solid #000;padding:3px 5px;text-align:right;">Taxable Value</th><th style="border:1px solid #000;padding:3px 5px;text-align:center;">CGST Rate</th><th style="border:1px solid #000;padding:3px 5px;text-align:right;">CGST Amount</th><th style="border:1px solid #000;padding:3px 5px;text-align:center;">SGST/UTGST Rate</th><th style="border:1px solid #000;padding:3px 5px;text-align:right;">SGST/UTGST Amount</th><th style="border:1px solid #000;padding:3px 5px;text-align:right;">Total Tax Amount</th></tr></thead><tbody>${Object.entries(
        hsnMap,
      )
        .map(
          ([hsn, data]) =>
            `<tr><td style="border:1px solid #000;padding:3px 5px;">${hsn}</td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${data.taxable.toFixed(2)}</td><td style="border:1px solid #000;padding:3px 5px;text-align:center;">${data.rate / 2}%</td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${data.cgst.toFixed(2)}</td><td style="border:1px solid #000;padding:3px 5px;text-align:center;">${data.rate / 2}%</td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${data.sgst.toFixed(2)}</td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${(data.cgst + data.sgst).toFixed(2)}</td></tr>`,
        )
        .join(
          "",
        )}<tr style="font-weight:bold;"><td style="border:1px solid #000;padding:3px 5px;">Total</td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${hsnTotalTaxable.toFixed(2)}</td><td style="border:1px solid #000;padding:3px 5px;"></td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${hsnTotalCGST.toFixed(2)}</td><td style="border:1px solid #000;padding:3px 5px;"></td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${hsnTotalSGST.toFixed(2)}</td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${hsnTotalTax.toFixed(2)}</td></tr></tbody></table>`;
  const amtWords = numberToWords(order.total || 0);
  const taxWords = numberToWords(hsnTotalTax);
  const roundOff = order.round_off || 0;
  const custName = order.customer_name || "Cash Customer";
  const custAddr = order.customer_address || "";
  const custGSTIN = order.customer_gstin || "";
  const custPhone = order.customer_phone || "";
  const buyerBlock = (label) =>
    `<div style="font-weight:bold;font-size:10px;text-decoration:underline;margin-bottom:4px;">${label}</div><div style="font-weight:bold;">${custName}</div>${custAddr ? `<div style="white-space:pre-line;font-size:10px;line-height:1.5;">${custAddr}</div>` : ""}${custPhone ? `<div style="font-size:10px;">Phone: ${custPhone}</div>` : ""}${custGSTIN ? `<div style="font-size:10px;">GSTIN/UIN: <b>${custGSTIN}</b></div>` : ""}<div style="font-size:10px;">State Name: ${COMPANY_SETTINGS.state}</div>`;
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>Tax Invoice - ${order.invoice_number}</title><style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:Arial,sans-serif;font-size:11px;width:210mm;margin:0 auto;padding:8mm;color:#000;}table{width:100%;border-collapse:collapse;}td,th{padding:3px 5px;vertical-align:top;}.outer-border{border:1px solid #000;}.bold{font-weight:bold;}.right{text-align:right;}.center{text-align:center;}@media print{@page{size:A4 portrait;margin:5mm;}body{width:auto;padding:0;-webkit-print-color-adjust:exact;print-color-adjust:exact;}}</style></head><body><table class="outer-border" style="border-bottom:none;"><tr><td colspan="2" style="border-bottom:1px solid #000;border-right:1px solid #000;padding:10px;text-align:center;"><div style="font-size:18px;font-weight:900;letter-spacing:2px;">${COMPANY_SETTINGS.name}</div><div style="font-size:10px;margin-top:2px;">${COMPANY_SETTINGS.address}</div><div style="font-size:10px;">Tel: ${COMPANY_SETTINGS.phone}</div><div style="font-size:10px;">GSTIN/UIN: <b>${COMPANY_SETTINGS.gstin}</b> &nbsp;|&nbsp; State Name: ${COMPANY_SETTINGS.state}</div><div style="font-size:10px;">E-Mail: ${COMPANY_SETTINGS.email}</div><div style="font-size:13px;font-weight:bold;text-decoration:underline;margin-top:5px;">Tax Invoice</div></td><td style="border-bottom:1px solid #000;padding:8px;width:36%;vertical-align:top;"><table style="width:100%;border:none;"><tr><td style="padding:2px 0;border:none;font-weight:bold;white-space:nowrap;">Invoice No.</td><td style="padding:2px 0;border:none;">: ${order.invoice_number}</td></tr><tr><td style="padding:2px 0;border:none;font-weight:bold;">Dated</td><td style="padding:2px 0;border:none;">: ${dateStr}</td></tr><tr><td style="padding:2px 0;border:none;font-weight:bold;">Time</td><td style="padding:2px 0;border:none;">: ${timeStr}</td></tr><tr><td style="padding:2px 0;border:none;font-weight:bold;white-space:nowrap;">Mode of Payment</td><td style="padding:2px 0;border:none;">: ${order.credit_card !== 0 ? "Card" : order.credit_cash !== 0 ? "Cash" : order.credit_upi !== 0 && "UPI"}</td></tr><tr><td style="padding:2px 0;border:none;font-weight:bold;">Cashier</td>${order.referred_by_staff_name ? `<tr><td style="padding:2px 0;border:none;font-weight:bold;white-space:nowrap;">Referred By</td><td style="padding:2px 0;border:none;">: ${order.referred_by_staff_name}</td></tr>` : ""}<td style="padding:2px 0;border:none;">: ${userName || "Staff"}</td></tr></table></td></tr><tr><td style="width:32%;border-right:1px solid #000;border-bottom:1px solid #000;padding:8px;">${buyerBlock("Consignee (Ship to)")}</td><td style="width:32%;border-right:1px solid #000;border-bottom:1px solid #000;padding:8px;">${buyerBlock("Buyer (Bill to)")}</td><td style="width:36%;border-bottom:1px solid #000;padding:8px;">${order.narration ? `<div><b>Narration:</b> ${order.narration}</div>` : ""}</td></tr></table><table class="outer-border" style="border-top:none;margin-top:-1px;"><thead><tr style="background:#f0f0f0;border-bottom:1px solid #000;"><th style="border-right:1px solid #aaa;width:4%;text-align:center;">Sl<br/>No.</th><th style="border-right:1px solid #aaa;width:31%;text-align:left;">Description of Goods</th><th style="border-right:1px solid #aaa;width:9%;text-align:center;">HSN/<br/>SAC</th><th style="border-right:1px solid #aaa;width:9%;text-align:center;">Quantity</th><th style="border-right:1px solid #aaa;width:10%;text-align:right;">Rate</th><th style="border-right:1px solid #aaa;width:5%;text-align:center;">Per</th><th style="border-right:1px solid #aaa;width:8%;text-align:center;">Disc.%</th><th style="width:12%;text-align:right;">Amount</th></tr></thead><tbody>${itemRowsHTML}<tr style="border-top:1px solid #000;"><td colspan="7" style="text-align:right;border-right:1px solid #aaa;padding:3px 5px;">${order.discount > 0 ? `<span style="font-size:10px;color:#444;">Discount Applied: ₹${(order.discount || 0).toFixed(2)}</span>` : "&nbsp;"}</td><td style="text-align:right;padding:3px 5px;">&nbsp;</td></tr></tbody></table><table class="outer-border" style="margin-top:-1px;border-top:none;"><tr><td style="width:62%;border-right:1px solid #000;" rowspan="3"><div style="font-weight:bold;margin-bottom:4px;">Amount Chargeable (in words)</div><div style="font-style:italic;">${amtWords}</div><div style="font-size:9px;margin-top:4px;color:#555;">E. &amp; O.E</div></td>${Math.abs(roundOff) >= 0.01 ? `<td style="text-align:right;border-right:1px solid #000;padding:3px 6px;width:26%;">Less : Round Off</td><td style="text-align:right;padding:3px 6px;width:12%;">${roundOff < 0 ? "(-)" : "(+)"} ₹${Math.abs(roundOff).toFixed(2)}</td>` : `<td style="border:none;" colspan="2"></td>`}</tr><tr><td style="text-align:right;font-weight:bold;font-size:13px;border-right:1px solid #000;border-top:1px solid #000;padding:4px 6px;">Total</td><td style="text-align:right;font-weight:bold;font-size:13px;border-top:1px solid #000;padding:4px 6px;">₹ ${(order.total || 0).toFixed(2)}</td></tr></table>${hsnTableHTML}<div class="outer-border" style="border-top:none;padding:5px;font-size:10px;font-style:italic;">Tax Amount (in words): <b>${taxWords}</b></div><table class="outer-border" style="margin-top:8px;"><tr><td style="width:34%;border-right:1px solid #000;vertical-align:top;padding:6px;"><div style="font-size:10px;font-weight:bold;">Company's PAN: ${COMPANY_SETTINGS.pan}</div><div style="font-size:10px;font-weight:bold;margin-top:8px;">Declaration</div><div style="font-size:9px;margin-top:3px;line-height:1.5;">We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.</div></td><td style="width:33%;border-right:1px solid #000;vertical-align:top;padding:6px;"><div style="font-size:10px;font-weight:bold;">Company's Bank Details</div><table style="margin-top:4px;font-size:10px;line-height:1.8;"><tr><td style="border:none;padding:0;white-space:nowrap;font-weight:bold;">Bank Name</td><td style="border:none;padding:0 0 0 6px;">: ${COMPANY_SETTINGS.bankName}</td></tr><tr><td style="border:none;padding:0;font-weight:bold;">A/c No.</td><td style="border:none;padding:0 0 0 6px;">: ${COMPANY_SETTINGS.bankAccount}</td></tr><tr><td style="border:none;padding:0;font-weight:bold;white-space:nowrap;">Branch &amp; IFS Code</td><td style="border:none;padding:0 0 0 6px;">: ${COMPANY_SETTINGS.bankBranch} &amp; ${COMPANY_SETTINGS.bankIFSC}</td></tr></table></td><td style="width:33%;text-align:center;vertical-align:bottom;padding:6px;"><div style="font-size:10px;font-weight:bold;">for ${COMPANY_SETTINGS.name}</div><div style="margin-top:32px;border-top:1px solid #000;padding-top:4px;font-size:10px;">Authorised Signatory</div></td></tr></table><div style="text-align:center;margin-top:6px;font-size:9px;color:#555;border-top:1px solid #ccc;padding-top:4px;">SUBJECT TO AHMEDABAD JURISDICTION &nbsp;|&nbsp; This is a Computer Generated Invoice</div></body></html>`;
};

const printInvoice = (order, userName) => {
  const html = order?.gst_type
    ? buildThermalGSTInvoice(order, userName)
    : buildNonGSTInvoice(order, userName);
  if (!html) return;
  const win = window.open("", "_blank", "width=420,height=700,scrollbars=yes");
  if (!win) {
    toast.error("Pop-up blocked! Please allow pop-ups.");
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.onload = () => {
    win.focus();
    win.print();
    setTimeout(() => win.close(), 600);
  };
};

// ── Barcode flash ──────────────────────────────────────────────
const BarcodeFlash = ({ show, productName }) => (
  <AnimatePresence>
    {show && (
      <motion.div
        initial={{ opacity: 0, y: -20, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.9 }}
        className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-emerald-500 text-white px-5 py-2.5 rounded-2xl shadow-2xl flex items-center gap-2.5 font-semibold pointer-events-none"
      >
        <Zap size={14} className="fill-white" />
        <span className="text-sm">{productName}</span>
        <CheckCircle2 size={14} />
      </motion.div>
    )}
  </AnimatePresence>
);

// ── Payment mode button (improved) ────────────────────────────
const PayBtn = ({ mode, active, onClick, t }) => {
  const Icon = PAYMENT_ICONS[mode];
  const modeColors = {
    cash: active ? "border-emerald-500 bg-emerald-500/10 text-emerald-400" : "",
    card: active ? "border-blue-500 bg-blue-500/10 text-blue-400" : "",
    upi: active ? "border-purple-500 bg-purple-500/10 text-purple-400" : "",
    split: active ? "border-amber-500 bg-amber-500/10 text-amber-400" : "",
    credit: active ? "border-rose-500 bg-rose-500/10 text-rose-400" : "",
    bank_transfer: active ? "border-cyan-500 bg-cyan-500/10 text-cyan-400" : "",
  };
  return (
    <button
      onClick={() => onClick(mode)}
      className={`flex-1 flex flex-col items-center gap-1 py-2 px-1 rounded-xl border-2 transition-all text-xs font-semibold min-w-0 ${
        active
          ? modeColors[mode] ||
            "border-amber-500 bg-amber-500/10 text-amber-400"
          : "border-stone-700 text-stone-500 hover:border-stone-600 hover:text-stone-400 bg-stone-800/50"
      }`}
    >
      <Icon size={15} />
      <span className="truncate w-full text-center leading-tight text-[10px]">
        {t(`pos.pay.${mode}`)}
      </span>
    </button>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN POS
// ═══════════════════════════════════════════════════════════════
const POS = () => {
  const { token, user } = useAuth();
  const { t } = useTranslation();
  const [googleReviewText, setGoogleReviewText] = useState("");
  const [productsLoading, setProductsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const PAGE_SIZE = 100;
  const [showPendingOrders, setShowPendingOrders] = useState(false);
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0);
  const [showCustomerOrders, setShowCustomerOrders] = useState(false);
  const [customerOrdersCount, setCustomerOrdersCount] = useState(0);
  const [showPurchaseOrders, setShowPurchaseOrders] = useState(false);
  const [poApprovalsCount, setPoApprovalsCount] = useState(0);
  const [showGodownApprovals, setShowGodownApprovals] = useState(false);
  const [godownApprovalsCount, setGodownApprovalsCount] = useState(0);

  // ── Products & search ────────────────────────────────────────
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const searchRef = useRef(null);
  const [scheduledDate, setScheduledDate] = useState("");
  const [enableScheduledDelivery, setEnableScheduledDelivery] = useState(false);
  const todayStr = () => new Date().toISOString().split("T")[0];
  // ── Cart ─────────────────────────────────────────────────────
  const [cart, setCart] = useState([]);
  const [heldOrders, setHeldOrders] = useState([]);

  // ── Invoice settings ─────────────────────────────────────────
  const [invoiceType, setInvoiceType] = useState("gst");
  const [gstType, setGstType] = useState("cgst_sgst");
  const [discountMode, setDiscountMode] = useState("percent");
  const [discountVal, setDiscountVal] = useState(0);
  const [paymentMode, setPaymentMode] = useState("");
  const [splitCash, setSplitCash] = useState(0);
  const [splitCard, setSplitCard] = useState(0);

  // ── Customer ─────────────────────────────────────────────────
  const [showCustomer, setShowCustomer] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerGSTIN, setCustomerGSTIN] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [returningCustomerInfo, setReturningCustomerInfo] = useState(null);
  const [lookingUpCustomer, setLookingUpCustomer] = useState(false);
  const [narration, setNarration] = useState("");
  const [referredByStaffId, setReferredByStaffId] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [creditBreakdown, setCreditBreakdown] = useState({
    cash: "",
    card: "",
    upi: "",
  });
  const [bankTransfer, setBankTransfer] = useState({
    accountName: "",
    accountNumber: "",
    ifscCode: "",
    amount: "",
  });

  const [schedule, setSchedule] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState("");
  // ── UI states ────────────────────────────────────────────────
  const [showScanner, setShowScanner] = useState(false);
  const [currentTempOrderId, setCurrentTempOrderId] = useState(null);

  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [showHoldPanel, setShowHoldPanel] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [flashProduct, setFlashProduct] = useState(null);
  const [barcodeBuffer, setBarcodeBuffer] = useState("");
  const barcodeTimer = useRef(null);
  const lastKeyTime = useRef(0);
  const [amountPaid, setAmountPaid] = useState(0);
  // ── Scanner-specific state ───────────────────────────────────
  const [scannerError, setScannerError] = useState(null);
  const [scannerMode, setScannerMode] = useState("camera"); // "camera" | "manual"
  const [manualBarcodeInput, setManualBarcodeInput] = useState("");
  const [scannerFacing, setScannerFacing] = useState("environment");
  const [transportCharges, setTransportCharges] = useState(0);

  const manualInputRef = useRef(null);

  const calculateDiscountedPrice = (basePrice, discount, discountType) => {
    if (!discount || discount <= 0) return basePrice;
    const raw =
      discountType === "percentage"
        ? basePrice - (basePrice * discount) / 100
        : basePrice - discount;
    return Math.round(raw * 100) / 100;
  };
  const totalAmount = useMemo(
    () => cart.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [cart],
  );
  const balanceDue = Math.max(0, totalAmount - amountPaid);
  const isFullPayment = amountPaid >= totalAmount;
  const isPartialPayment = amountPaid > 0 && amountPaid < totalAmount;

  // ── Filtered products ─────────────────────────────────────────
  const filteredProducts = useMemo(
    () =>
      products.filter((p) => {
        const matchSearch =
          !searchQuery ||
          p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.category?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchCat =
          activeCategory === "all" || p.category === activeCategory;
        return matchSearch && matchCat;
      }),
    [products, searchQuery, activeCategory],
  );
  const loadTemporaryOrder = (tempOrder) => {
    if (cart.length > 0) {
      toast.error("Please clear current cart first");
      return;
    }

    // Temporary orders store the already-discounted unit price together with
    // the product discount. POS calculations apply the discount to
    // `selling_price`, so restore the pre-discount base price here to avoid
    // applying the same product discount a second time.
    const restoreBasePrice = (price, discount, discountType) => {
      const savedPrice = Number(price || 0);
      const savedDiscount = Number(discount || 0);
      if (savedDiscount <= 0) return savedPrice;
      if (discountType === "percentage" && savedDiscount < 100) {
        return Math.round((savedPrice / (1 - savedDiscount / 100)) * 100) / 100;
      }
      return discountType === "amount"
        ? savedPrice + savedDiscount
        : savedPrice;
    };

    // Map temporary order items to cart format
    const cartItems = tempOrder.items.map((item) => ({
      id: item.product_id,
      name: item.product_name,
      selling_price: restoreBasePrice(
        item.price,
        item.discount,
        item.discount_type,
      ),
      quantity: item.quantity,
      discount: item.discount || 0,
      discount_type: item.discount_type || "percentage",
      purchased: item.purchased === true,
      // You may need to fetch full product details for other fields
      // For now, using minimal data
      stock_quantity: 999, // placeholder
    }));

    setCart(cartItems);
    setCustomerName(tempOrder.customer_name || "");
    setCustomerPhone(tempOrder.customer_phone || "");
    setReferredByStaffId(tempOrder.referred_by_staff_id || "");

    // Restore the saved payment fields and select the corresponding payment tab.
    // Quick POS stores the entered amounts in payment_breakdown, so relying only
    // on the controlled text fields left the POS tab at its default value.
    const breakdown = tempOrder.payment_breakdown || {};
    const bankAmount =
      Number(
        breakdown.bank_transfer ??
          breakdown.bank ??
          tempOrder.bank_transfer_amount ??
          0,
      ) || 0;
    const paymentAmounts = {
      cash: Number(breakdown.cash || 0) || 0,
      card: Number(breakdown.card || 0) || 0,
      upi: Number(breakdown.upi || 0) || 0,
      bank_transfer: bankAmount,
    };
    const paidModes = Object.entries(paymentAmounts)
      .filter(([, amount]) => amount > 0)
      .map(([mode]) => mode);
    const modeToSelect = paidModes.length > 1
      ? "split"
      : paidModes.length === 1
        ? paidModes[0]
        : "credit";
    setPaymentMode(modeToSelect);
    setCreditBreakdown({
      cash: String(paymentAmounts.cash || ""),
      card: String(paymentAmounts.card || ""),
      upi: String(paymentAmounts.upi || ""),
    });
    if (
      bankAmount > 0 ||
      tempOrder.bank_account_name ||
      tempOrder.bank_account_number ||
      tempOrder.bank_ifsc_code
    ) {
      setBankTransfer({
        accountName: tempOrder.bank_account_name || "",
        accountNumber: tempOrder.bank_account_number || "",
        ifscCode: tempOrder.bank_ifsc_code || "",
        amount: String(bankAmount || ""),
      });
    }
    setCurrentTempOrderId(tempOrder.id);
    toast.success(`Loaded order ${tempOrder.temp_order_number}`);
  };
  // Add auto-fetch for pending count
  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const res = await axios.get(`${API_URL}/temporary-orders`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { status: "pending" },
        });
        setPendingOrdersCount(res.data.filter((order) => order.source !== "customer_store").length);
        setCustomerOrdersCount(res.data.filter((order) => order.source === "customer_store").length);
        
        const poRes = await axios.get(`${API_URL}/purchase-orders`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { status: "Pending Approval" },
        });
        setPoApprovalsCount(poRes.data.data ? poRes.data.data.length : 0);

        const godownRes = await axios.get(`${API_URL}/orders`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { status: "Godown Pending Approval" },
        });
        setGodownApprovalsCount(godownRes.data.orders ? godownRes.data.orders.length : (godownRes.data.length || 0));
      } catch (err) {
        console.error("Failed to fetch pending count:", err);
      }
    };

    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 5000);
    return () => clearInterval(interval);
  }, [token]);

  // ── Fetch products + staff ───────────────────────────────────
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async (page = 1) => {
    try {
      const [prodRes, staffRes] = await Promise.allSettled([
        axios.get(`${API_URL}/products`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { page, limit: PAGE_SIZE, search: searchQuery },
        }),
        axios.get(`${API_URL}/users`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (prodRes.status === "fulfilled") {
        setProducts(prodRes.value.data.products);
        setCurrentPage(prodRes.value.data.pagination.page || page);
        setTotalPages(prodRes.value.data.pagination.pages || 1);
        setTotalProducts(prodRes.value.data.pagination.total || 0);
      } else toast.error(t("pos.fetchFailed"));
      if (staffRes.status === "fulfilled") {
        const all = Array.isArray(staffRes.value.data)
          ? staffRes.value.data
          : [];
        setStaffList(
          all.filter((u) => ["staff", "billing", "manager"].includes(u.role)),
        );
      }
    } catch {
      toast.error(t("pos.fetchFailed"));
    }
  };

  // ── Categories ────────────────────────────────────────────────
  const categories = useMemo(() => {
    const cats = [
      ...new Set(products.map((p) => p.category).filter(Boolean)),
    ].sort();
    return ["all", ...cats];
  }, [products]);

  const categoryCartCount = useMemo(() => {
    const map = {};
    cart.forEach((item) => {
      const cat = item.category || "Uncategorized";
      map[cat] = (map[cat] || 0) + item.quantity;
    });
    return map;
  }, [cart]);

  // ── External barcode ─────────────────────────────────────────
  // useEffect(() => {
  //   const handleKeyDown = (e) => {
  //     const tag = document.activeElement?.tagName?.toLowerCase();
  //     const isInput = tag === "input" || tag === "textarea";
  //     const now = Date.now();
  //     const gap = now - lastKeyTime.current;
  //     lastKeyTime.current = now;
  //     if (e.key === "Enter") {
  //       const buf = barcodeBuffer.trim();
  //       if (buf.length >= 4) {
  //         e.preventDefault();
  //         processBarcode(buf);
  //         setBarcodeBuffer("");
  //       }
  //       return;
  //     }
  //     if (isInput && gap > 80) return;
  //     if (e.key.length === 1) {
  //       setBarcodeBuffer((prev) => (gap > 500 ? e.key : prev + e.key));
  //       if (barcodeTimer.current) clearTimeout(barcodeTimer.current);
  //       barcodeTimer.current = setTimeout(() => setBarcodeBuffer(""), 2000);
  //     }
  //   };
  //   window.addEventListener("keydown", handleKeyDown);
  //   return () => {
  //     window.removeEventListener("keydown", handleKeyDown);
  //     if (barcodeTimer.current) clearTimeout(barcodeTimer.current);
  //   };
  // }, [barcodeBuffer]);

  useEffect(() => {
    setCurrentPage(1);
    fetchProducts(1);
  }, [searchQuery, activeCategory]);

  // const processBarcode = useCallback(
  //   async (code) => {
  //     try {
  //       const res = await axios.get(`${API_URL}/products/barcode/${code}`, {
  //         headers: { Authorization: `Bearer ${token}` },
  //       });
  //       addToCartDirect(res.data);
  //       setFlashProduct(res.data.name);
  //       setTimeout(() => setFlashProduct(null), 2000);
  //     } catch {
  //       toast.error(`${t("pos.barcodeNotFound")}: ${code}`);
  //     }
  //   },
  //   [token],
  // );
  const processBarcode = useCallback(
    async (code) => {
      try {
        const cleanedCode = String(code).trim();

        const res = await axios.get(
          `${API_URL}/products/barcode/${cleanedCode}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        addToCartDirect(res.data);

        setFlashProduct(res.data.name);

        toast.success(`Added: ${res.data.name}`);

        setTimeout(() => {
          setFlashProduct(null);
        }, 1500);
      } catch (err) {
        console.log("SCAN ERROR:", err);

        toast.error(`Barcode not found: ${code}`);
      }
    },
    [token],
  );

  useEffect(() => {
    let scanBuffer = "";
    let scanTimeout;

    const handleHoneywellScan = async (e) => {
      // Ignore modifier keys
      if (
        e.key === "Shift" ||
        e.key === "Control" ||
        e.key === "Alt" ||
        e.key === "Meta"
      ) {
        return;
      }

      // Prevent React inputs from consuming scanner keys
      e.stopPropagation();

      // Scanner sends ENTER after scan
      if (e.key === "Enter") {
        const barcode = scanBuffer.trim();

        if (barcode.length >= 3) {
          console.log("SCANNED:", barcode);

          processBarcode(barcode);
        }

        scanBuffer = "";

        return;
      }

      // Only printable chars
      if (e.key.length === 1) {
        scanBuffer += e.key;
      }

      clearTimeout(scanTimeout);

      scanTimeout = setTimeout(() => {
        scanBuffer = "";
      }, 200);
    };

    window.addEventListener("keydown", handleHoneywellScan, true);

    return () => {
      window.removeEventListener("keydown", handleHoneywellScan, true);
    };
  }, [processBarcode]);

  // ── Cart helpers ──────────────────────────────────────────────
  const addToCartDirect = (product) => {
    setCart((prev) => {
      const ex = prev.find((i) => i.id === product.id);
      if (ex) {
        if (ex.quantity >= product.stock_quantity) {
          toast.error(t("pos.notEnoughStock"));
          return prev;
        }
        return prev.map((i) =>
          i.id === product.id ? { ...i, quantity: i.quantity + 1 } : i,
        );
      }
      if (product.stock_quantity === 0) {
        toast.error(t("pos.outOfStock"));
        return prev;
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const updateQuantity = (id, delta) => {
    setCart((prev) =>
      prev
        .map((item) => {
          if (item.id !== id) return item;
          const nq = item.quantity + delta;
          if (nq <= 0) return null;
          if (nq > item.stock_quantity) {
            toast.error(t("pos.notEnoughStock"));
            return item;
          }
          return { ...item, quantity: nq };
        })
        .filter(Boolean),
    );
  };

  const setQuantityDirect = (id, val) => {
    const n = parseInt(val) || 1;
    setCart((prev) =>
      prev
        .map((item) => {
          if (item.id !== id) return item;
          if (n > item.stock_quantity) {
            toast.error(t("pos.notEnoughStock"));
            return item;
          }
          if (n <= 0) return null;
          return { ...item, quantity: n };
        })
        .filter(Boolean),
    );
  };

  const removeFromCart = (id) =>
    setCart((prev) => prev.filter((i) => i.id !== id));

  const togglePurchased = (id) =>
    setCart((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, purchased: item.purchased !== true } : item,
      ),
    );

  // Customer lookup by phone number (prefill name, address, GSTIN - exclude staff referral)
  useEffect(() => {
    const cleanPhone = (customerPhone || "").replace(/\D/g, "");
    if (cleanPhone.length < 10) {
      setReturningCustomerInfo(null);
      return;
    }
    const timer = setTimeout(async () => {
      setLookingUpCustomer(true);
      try {
        const res = await axios.get(`${API_URL}/customers/lookup`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { phone: cleanPhone },
        });
        if (res.data?.found) {
          setReturningCustomerInfo(res.data);
          // Only auto-fill if the fields are currently empty or matching old values
          setCustomerName((prev) => (!prev.trim() ? res.data.customer_name || "" : prev));
          if (res.data.customer_address) {
            setCustomerAddress((prev) => (!prev.trim() ? res.data.customer_address : prev));
          }
          if (res.data.customer_gstin) {
            setCustomerGSTIN((prev) => (!prev.trim() ? res.data.customer_gstin : prev));
          }
          toast.success(`Returning customer found: ${res.data.customer_name} (${res.data.total_orders} past orders)`);
        } else {
          setReturningCustomerInfo(null);
        }
      } catch (err) {
        /* soft fail */
      } finally {
        setLookingUpCustomer(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [customerPhone, token]);

  const clearCart = () => {
    setCart([]);
    setDiscountVal(0);
    setCustomerName("");
    setCustomerPhone("");
    setCustomerGSTIN("");
    setCustomerAddress("");
    setReturningCustomerInfo(null);
    setNarration("");
    setReferredByStaffId("");
    setPaymentMode("");
    setCreditBreakdown({ cash: "", card: "", upi: "" });
    setBankTransfer({
      accountName: "",
      accountNumber: "",
      ifscCode: "",
      amount: "",
    });
    setTransportCharges(0);
    setGoogleReviewText("");
    setCurrentTempOrderId(null);
  };

  const addAllFromCategory = useCallback(() => {
    if (activeCategory === "all") return;
    let added = 0;
    filteredProducts.forEach((product) => {
      if (product.stock_quantity === 0) return;
      if (cart.find((i) => i.id === product.id)) return;
      setCart((prev) => {
        const ex = prev.find((i) => i.id === product.id);
        if (ex) return prev;
        return [...prev, { ...product, quantity: 1 }];
      });
      added++;
    });
    if (added > 0)
      toast.success(
        `Added ${added} product${added !== 1 ? "s" : ""} from "${activeCategory}"`,
      );
    else
      toast.info(
        "All available products from this category are already in cart",
      );
  }, [activeCategory, filteredProducts, cart]);

  const removeAllFromCategory = useCallback(() => {
    if (activeCategory === "all") return;
    const catIds = new Set(filteredProducts.map((p) => p.id));
    const removed = cart.filter((i) => catIds.has(i.id)).length;
    if (removed === 0) {
      toast.info("No products from this category are in cart");
      return;
    }
    setCart((prev) => prev.filter((i) => !catIds.has(i.id)));
    toast.success(
      `Removed ${removed} item${removed !== 1 ? "s" : ""} from "${activeCategory}"`,
    );
  }, [activeCategory, filteredProducts, cart]);

  const holdCart = () => {
    if (cart.length === 0) return;
    const label = customerName || `Hold ${heldOrders.length + 1}`;
    setHeldOrders((prev) => [
      ...prev,
      {
        id: Date.now(),
        cart: [...cart],
        label,
        customerName,
        customerPhone,
        customerGSTIN,
        customerAddress,
        narration,
        referredByStaffId,
        paymentMode,
        creditBreakdown: { ...creditBreakdown },
        bankTransfer: { ...bankTransfer },
        discountVal,
        discountMode,
        invoiceType,
        gstType,
        transportCharges,
      },
    ]);
    clearCart();
    toast.success(t("pos.orderHeld"));
  };

  const resumeHeld = (held) => {
    if (cart.length > 0) {
      toast.error(t("pos.clearCartFirst"));
      return;
    }
    setCart(held.cart);
    setCustomerName(held.customerName || "");
    setCustomerPhone(held.customerPhone || "");
    setCustomerGSTIN(held.customerGSTIN || "");
    setCustomerAddress(held.customerAddress || "");
    setNarration(held.narration || "");
    setReferredByStaffId(held.referredByStaffId || "");
    setPaymentMode(held.paymentMode || "cash");
    setCreditBreakdown(held.creditBreakdown || { cash: "", card: "", upi: "" });
    setBankTransfer(
      held.bankTransfer || {
        accountName: "",
        accountNumber: "",
        ifscCode: "",
        amount: "",
      },
    );
    setDiscountVal(held.discountVal || 0);
    setDiscountMode(held.discountMode || "percent");
    setInvoiceType(held.invoiceType || "gst");
    setGstType(held.gstType || "cgst_sgst");
    setTransportCharges(held.transportCharges || 0);
    setHeldOrders((prev) => prev.filter((h) => h.id !== held.id));
    setShowHoldPanel(false);
  };

  // ── Calculations ──────────────────────────────────────────────
  const subtotal =
    Math.round(
      cart.reduce(
        (s, i) =>
          s +
          calculateDiscountedPrice(
            i.selling_price,
            i.discount,
            i.discount_type,
          ) *
            i.quantity,
        0,
      ) * 100,
    ) / 100;
  const itemDiscountTotal = cart.reduce((sum, item) => {
    const discount = Number(item.discount || 0);
    const quantity = Number(item.quantity || 0);
    const finalUnitPrice = calculateDiscountedPrice(
      item.selling_price,
      discount,
      item.discount_type,
    );
    const originalUnitPrice = Number(item.selling_price || 0);
    return sum + Math.max(0, originalUnitPrice - finalUnitPrice) * quantity;
  }, 0);
  const discountAmount =
    discountMode === "percent"
      ? (subtotal * parseFloat(discountVal || 0)) / 100
      : Math.min(parseFloat(discountVal || 0), subtotal);
  const afterDiscount = subtotal - discountAmount;
  const amountBeforeDiscount = subtotal + itemDiscountTotal;
  const totalDiscount = itemDiscountTotal + discountAmount;
  const safeTransport = parseFloat(transportCharges || 0) || 0;

  const gstBreakdown = useMemo(() => {
    if (invoiceType !== "gst") return {};
    const factor = subtotal > 0 ? afterDiscount / subtotal : 1;
    const map = {};
    cart.forEach((i) => {
      const rate = i.gst_rate ?? 0;
      const inclusiveAmt =
        calculateDiscountedPrice(i.selling_price, i.discount, i.discount_type) *
        i.quantity *
        factor;
      const gstAmt = rate > 0 ? (inclusiveAmt * rate) / (100 + rate) : 0;
      if (rate > 0) map[rate] = (map[rate] || 0) + gstAmt;
    });
    return map;
  }, [cart, invoiceType, afterDiscount, subtotal]);

  const taxAmount = Object.values(gstBreakdown).reduce((s, v) => s + v, 0);
  const preRound = Number((afterDiscount + safeTransport).toFixed(2));
  const roundedTotal = Math.round(preRound);
  const roundOff = roundedTotal - preRound;
  const total = Number(roundedTotal.toFixed(2));

  // Sync simple payment modes to total
  useEffect(() => {
    if (["cash", "card", "upi"].includes(paymentMode)) {
      setCreditBreakdown({
        cash: "",
        card: "",
        upi: "",
        [paymentMode]: String(total),
      });
    }
  }, [total, paymentMode]);

  const amountReceived = useMemo(() => {
    const received =
      parseFloat(creditBreakdown.cash || 0) +
      parseFloat(creditBreakdown.card || 0) +
      parseFloat(creditBreakdown.upi || 0) +
      parseFloat(bankTransfer.amount || 0);
    return Math.round(received * 100) / 100;
  }, [
    creditBreakdown.cash,
    creditBreakdown.card,
    creditBreakdown.upi,
    bankTransfer.amount,
    total,
  ]);

  const splitTotal =
    parseFloat(creditBreakdown.cash || 0) +
    parseFloat(creditBreakdown.card || 0) +
    parseFloat(creditBreakdown.upi || 0) +
    parseFloat(bankTransfer.amount || 0);
  const splitValid =
    paymentMode !== "split" || Math.abs(splitTotal - total) < 0.01;

  // ── Checkout ──────────────────────────────────────────────────
  const handleCheckout = async () => {
    console.log("calling");
    if (cart.length === 0) {
      toast.error(t("pos.cartEmpty"));
      return;
    }
    if (!paymentMode) {
      toast.error("Please select a Payment Mode (Cash, Card, UPI, Split, or Bank Transfer) before checkout");
      return;
    }
    if (!customerPhone.trim()) {
      setShowCustomer(true);
      toast.error("Customer phone number is required before checkout");
      return;
    }
    if (
      paymentMode === "bank_transfer" &&
      (!bankTransfer.accountName.trim() ||
        !bankTransfer.accountNumber.trim() ||
        !bankTransfer.ifscCode.trim())
    ) {
      toast.error("Enter account name, account number and IFSC code");
      return;
    }
    if (paymentMode === "split" && !splitValid) {
      toast.error("Split payment amounts must equal the order total");
      return;
    }
    if (!googleReviewText.trim()) {
      toast.error("Please add the customer's Google review before checkout");
      return;
    }
    const productTotal = afterDiscount;
    const creditAmt = Math.max(
      0,
      Math.round((productTotal - (amountReceived || 0)) * 100) / 100,
    );

    setCheckingOut(true);
    try {
      const isCgstSgst = invoiceType === "gst" && gstType === "cgst_sgst";
      const isIgst = invoiceType === "gst" && gstType === "igst";
      const discFactor = subtotal > 0 ? afterDiscount / subtotal : 1;
      const payload = {
        items: cart.map((i) => {
          const itemTotal =
            calculateDiscountedPrice(
              i.selling_price,
              i.discount,
              i.discount_type,
            ) * i.quantity;
          const inclusiveAmt = itemTotal * discFactor;
          const rate = invoiceType === "gst" ? (i.gst_rate ?? 0) : 0;
          const gstAmt = rate > 0 ? (inclusiveAmt * rate) / (100 + rate) : 0;
          const taxableAmt = inclusiveAmt - gstAmt;
          return {
            product_id: i.id,
            product_name: i.name,
            image_url: i.image_url || "",
            quantity: i.quantity,
            purchased: i.purchased === true,
            price: calculateDiscountedPrice(
              i.selling_price,
              i.discount,
              i.discount_type,
            ),
            total: itemTotal,
            discount: Number(i.discount || 0),
            discount_type: i.discount_type || "percentage",
            gst_rate: rate,
            taxable: taxableAmt,
            cgst: isCgstSgst ? gstAmt / 2 : 0,
            sgst: isCgstSgst ? gstAmt / 2 : 0,
            igst: isIgst ? gstAmt : 0,
          };
        }),
        google_review: googleReviewText,
        transport_charges: safeTransport,
        subtotal,
        tax: taxAmount,
        discount: discountAmount,
        total,
        round_off: roundOff,
        cgst: isCgstSgst ? taxAmount / 2 : 0,
        sgst: isCgstSgst ? taxAmount / 2 : 0,
        igst: isIgst ? taxAmount : 0,
        total_tax: taxAmount,
        payment_mode:
          paymentMode === "bank_transfer"
            ? "Bank Transfer"
            : paymentMode === "cash"
              ? "Cash"
              : paymentMode === "card"
                ? "Card"
                : paymentMode === "upi"
                  ? "UPI"
                  : paymentMode === "split"
                    ? "Split"
                  : "Credit",
        gst_type: invoiceType === "gst" ? gstType : null,
        invoice_type: invoiceType,
        customer_name: customerName || null,
        customer_phone: customerPhone || null,
        customer_gstin: customerGSTIN || null,
        customer_address: customerAddress || null,
        narration: narration || null,
        salesman_id: user?.id,
        referred_by_staff_id: referredByStaffId || null,
        amount_received: amountReceived || 0,
        amount_paid: amountReceived || 0,
        credit_amount: creditAmt,
        credit_cash:
          amountReceived < total || paymentMode === "split" || paymentMode === "credit"
            ? Number(creditBreakdown.cash || 0)
            : paymentMode === "cash" ? amountReceived || 0 : 0,
        credit_card:
          amountReceived < total || paymentMode === "split" || paymentMode === "credit"
            ? Number(creditBreakdown.card || 0)
            : paymentMode === "card" ? amountReceived || 0 : 0,
        credit_upi:
          amountReceived < total || paymentMode === "split" || paymentMode === "credit"
            ? Number(creditBreakdown.upi || 0)
            : paymentMode === "upi" ? amountReceived || 0 : 0,
        bank_account_name:
          paymentMode === "bank_transfer"
            ? bankTransfer.accountName.trim()
            : "",
        bank_account_number:
          paymentMode === "bank_transfer"
            ? bankTransfer.accountNumber.trim()
            : "",
        bank_ifsc_code:
          paymentMode === "bank_transfer"
            ? bankTransfer.ifscCode.trim().toUpperCase()
            : "",
        scheduled_delivery_date: enableScheduledDelivery ? scheduledDate : null,
        status:
          creditAmt > 0
            ? "partial" // payment pending
            : scheduledDate
              ? "pending_delivery" // paid but delivery later
              : "delivered", // fully done

        // optional (recommended)
        delivered_at:
          creditAmt === 0 && !scheduledDate ? new Date().toISOString() : null,
      };
      const res = await axios.post(`${API_URL}/orders`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (currentTempOrderId) {
        try {
          await axios.delete(
            `${API_URL}/temporary-orders/${currentTempOrderId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          console.log("Temporary order deleted successfully");
          setCurrentTempOrderId(null); // Clear the temp order ID
        } catch (tempErr) {
          console.error("Failed to delete temporary order:", tempErr);
          // Don't show error to user since main order succeeded
        }
      }
      // if (currentTempOrderId) {
      //   await axios.put(
      //     `${API_URL}/temporary-orders/${currentTempOrderId}`,
      //     {
      //       status: "completed",
      //       completed_order_id: res.data.id,
      //     },
      //     { headers: { Authorization: `Bearer ${token}` } },
      //   );
      // }
      res.data.gst_type = invoiceType === "gst" ? gstType : null;
      res.data.customer_name = customerName;
      res.data.customer_phone = customerPhone;
      res.data.customer_gstin = customerGSTIN;
      res.data.customer_address = customerAddress;
      res.data.payment_mode = paymentMode;
      res.data.narration = narration;
      res.data.round_off = roundOff;
      res.data.referred_by_staff_name =
        staffList.find((s) => s.id === referredByStaffId)?.name || null;
      setLastOrder(res.data);
      setCheckingOut(false);
      clearCart();
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3500);
      fetchProducts();
      if (creditAmt > 0.01) {
        setTimeout(() => {
          toast.success(
            `Credit of ₹${creditAmt.toFixed(2)} posted to ${customerName}'s ledger`,
            { duration: 5000 },
          );
        }, 500);
      }
    } catch (eee) {
      console.error(eee);
      toast.error(t("pos.checkoutFailed"));
    } finally {
      setCheckingOut(false);
    }
  };

  // ── Camera scan ───────────────────────────────────────────────
  const handleCameraScan = async (result) => {
    if (result?.[0]?.rawValue) {
      const code = result[0].rawValue;
      try {
        const res = await axios.get(`${API_URL}/products/barcode/${code}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        addToCartDirect(res.data);
        setShowScanner(false);
        setScannerError(null);
        toast.success(`${t("pos.added")}: ${res.data.name}`);
      } catch {
        toast.error(t("pos.productNotFound"));
      }
    }
  };

  // ── Manual barcode submit ─────────────────────────────────────
  const handleManualBarcodeSubmit = async () => {
    const code = manualBarcodeInput.trim();
    if (!code) return;
    try {
      const res = await axios.get(`${API_URL}/products/barcode/${code}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      addToCartDirect(res.data);
      setShowScanner(false);
      setManualBarcodeInput("");
      setScannerError(null);
      toast.success(`${t("pos.added")}: ${res.data.name}`);
    } catch {
      toast.error(`${t("pos.barcodeNotFound")}: ${code}`);
    }
  };

  const addableCount = useMemo(() => {
    if (activeCategory === "all") return 0;
    return filteredProducts.filter(
      (p) => p.stock_quantity > 0 && !cart.find((c) => c.id === p.id),
    ).length;
  }, [filteredProducts, cart, activeCategory]);

  const categoryInCartCount = useMemo(() => {
    if (activeCategory === "all") return 0;
    const catIds = new Set(filteredProducts.map((p) => p.id));
    return cart
      .filter((i) => catIds.has(i.id))
      .reduce((s, i) => s + i.quantity, 0);
  }, [filteredProducts, cart, activeCategory]);

  const cartItemCount = cart.reduce((s, i) => s + i.quantity, 0);

  // ── Checkout button disabled logic ────────────────────────────
  const isCheckoutDisabled = () => {
    if (cart.length === 0) return true;

    if (checkingOut) return true;
    if (!paymentMode) return true;
    if (!googleReviewText.trim()) return true;
    if (paymentMode === "split" && !splitValid) return true;
    if (paymentMode === "credit") {
      if (referredByStaffId.length === 0) {
        return true;
      }

      // Customer name + phone required
      if (!customerName.trim()) return true;

      if (!customerPhone.trim()) return true;

      const breakDownSum =
        parseFloat(creditBreakdown.cash || 0) +
        parseFloat(creditBreakdown.card || 0) +
        parseFloat(creditBreakdown.upi || 0) +
        parseFloat(bankTransfer.amount || 0);
      
      const amtRcvd = parseFloat(amountReceived) || 0;
      
      // Breakdown must exactly equal amountReceived
      if (Math.abs(breakDownSum - amtRcvd) > 0.01) return true;
    }

    return false;
  };

  // ──────────────────────────────────────────────────────────────
  return (
    <div
      className="flex flex-col lg:flex-row h-auto lg:h-[calc(100vh)]"
      data-testid="pos-page"
    >
      <BarcodeFlash show={!!flashProduct} productName={flashProduct} />

      {/* ── Order success overlay ── */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 backdrop-blur-sm pointer-events-none"
          >
            <motion.div
              initial={{ scale: 0.7, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.7, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 22 }}
              className="bg-white rounded-3xl shadow-2xl p-10 flex flex-col items-center gap-3 pointer-events-none mx-4"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1, type: "spring", stiffness: 400 }}
                className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center"
              >
                <CheckCircle2 size={44} className="text-emerald-500" />
              </motion.div>
              <p className="text-2xl font-bold text-stone-800">
                {t("pos.orderComplete")}
              </p>
              <p className="text-stone-400 text-sm">
                {t("pos.invoice")} #{lastOrder?.invoice_number}
              </p>
              <p className="text-3xl font-mono font-bold text-emerald-600">
                ₹{lastOrder?.total?.toFixed(2)}
              </p>
              <span className="text-xs bg-stone-100 text-stone-500 px-3 py-1 rounded-full capitalize font-medium">
                {lastOrder?.payment_mode}
              </span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══════════ LEFT: Product Grid ═══════════ */}
      <div className="w-full lg:w-[62%] flex flex-col overflow-hidden bg-stone-50">
        {/* Header */}
        <div className="px-4 md:px-6 pt-4 pb-3 bg-white border-b border-stone-100 shadow-sm">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <h1 className="text-xl md:text-2xl font-bold tracking-tight text-stone-800 leading-tight">
                {t("pos.title")}
              </h1>
              <p className="text-[11px] text-stone-400 mt-0.5 flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                {t("pos.scannerReady")}
              </p>
            </div>
            {pendingOrdersCount > 0 && (
              <button
                onClick={() => setShowPendingOrders(true)}
                className="relative flex items-center gap-1.5 text-xs font-semibold text-orange-700 bg-orange-50 border border-orange-200 px-3 py-2 rounded-xl hover:bg-orange-100 transition-colors"
              >
                <Clock size={14} />
                <span className="hidden sm:inline">Pending</span>
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-orange-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {pendingOrdersCount}
                </span>
              </button>
            )}
            {poApprovalsCount > 0 && (
              <button
                onClick={() => setShowPurchaseOrders(true)}
                className="relative flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-3 py-2 rounded-xl hover:bg-blue-100 transition-colors"
              >
                <Package size={14} />
                <span className="hidden sm:inline">PO Approvals</span>
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-blue-600 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {poApprovalsCount}
                </span>
              </button>
            )}
            {godownApprovalsCount > 0 && (
              <button
                onClick={() => setShowGodownApprovals(true)}
                className="relative flex items-center gap-1.5 text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded-xl hover:bg-rose-100 transition-colors"
              >
                <CheckCircle2 size={14} />
                <span className="hidden sm:inline">Godown Approvals</span>
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-rose-600 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {godownApprovalsCount}
                </span>
              </button>
            )}
            {customerOrdersCount > 0 && (
              <button
                onClick={() => setShowCustomerOrders(true)}
                className="relative flex items-center gap-1.5 text-xs font-semibold text-violet-700 bg-violet-50 border border-violet-200 px-3 py-2 rounded-xl hover:bg-violet-100 transition-colors"
              >
                <Package size={14} />
                <span className="hidden sm:inline">Customer Orders</span>
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-violet-600 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {customerOrdersCount}
                </span>
              </button>
            )}
            <div className="flex items-center gap-2">
              {heldOrders.length > 0 && (
                <button
                  onClick={() => setShowHoldPanel(true)}
                  className="relative flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-xl hover:bg-amber-100 transition-colors"
                >
                  <PauseCircle size={14} />
                  <span className="hidden sm:inline">{t("pos.held")}</span>
                  <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-amber-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                    {heldOrders.length}
                  </span>
                </button>
              )}
              <button
                onClick={() => {
                  setScannerError(null);
                  setScannerMode("camera");
                  setManualBarcodeInput("");
                  setShowScanner(true);
                }}
                className="flex items-center gap-2 text-sm font-semibold bg-stone-800 text-white px-4 py-2 rounded-xl hover:bg-stone-700 active:scale-95 transition-all"
              >
                <Scan size={15} />
                <span className="hidden sm:inline">{t("pos.scanBarcode")}</span>
                <span className="sm:hidden">Scan</span>
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search
              size={14}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400"
            />
            <input
              ref={searchRef}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("pos.searchPlaceholder")}
              className="w-full pl-9 pr-9 h-10 rounded-xl border border-stone-200 bg-stone-50 text-sm outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-300 transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* ── Category tabs + action bar ────────────────────────── */}
        <div className="bg-white border-b border-stone-100 shrink-0">
          <div className="px-4 md:px-6 pt-2.5 pb-2 overflow-x-auto flex gap-1.5 scrollbar-hide">
            {categories.map((cat) => {
              const count =
                cat === "all" ? cartItemCount : categoryCartCount[cat] || 0;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`shrink-0 relative px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all ${
                    activeCategory === cat
                      ? "bg-stone-800 text-white"
                      : "bg-stone-100 text-stone-500 hover:bg-stone-200"
                  }`}
                >
                  {cat === "all" ? t("pos.allProducts") : cat}
                  {count > 0 && (
                    <span
                      className={`absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-black flex items-center justify-center leading-none ${
                        activeCategory === cat
                          ? "bg-amber-400 text-stone-900"
                          : "bg-stone-700 text-white"
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <AnimatePresence>
            {activeCategory !== "all" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.15 }}
                className="overflow-hidden"
              >
                <div className="px-4 md:px-6 pb-2 flex items-center justify-between gap-2">
                  <p className="text-[11px] text-stone-400 truncate">
                    <span className="font-bold text-stone-600 capitalize">
                      {activeCategory}
                    </span>
                    {" · "}
                    {filteredProducts.length} product
                    {filteredProducts.length !== 1 ? "s" : ""}
                    {categoryInCartCount > 0 && (
                      <span className="text-amber-600 font-bold">
                        {" · "}
                        {categoryInCartCount} in cart
                      </span>
                    )}
                  </p>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {addableCount > 0 && (
                      <button
                        onClick={addAllFromCategory}
                        className="flex items-center gap-1.5 text-[11px] font-bold bg-stone-800 hover:bg-stone-700 text-white px-2.5 py-1.5 rounded-lg transition-colors"
                      >
                        <PlusCircle size={12} />
                        Add all ({addableCount})
                      </button>
                    )}
                    {categoryInCartCount > 0 && (
                      <button
                        onClick={removeAllFromCategory}
                        className="flex items-center gap-1.5 text-[11px] font-bold text-rose-600 bg-rose-50 border border-rose-200 hover:bg-rose-100 px-2.5 py-1.5 rounded-lg transition-colors"
                      >
                        <MinusCircle size={12} />
                        Remove all
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Product grid */}
        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4">
          {productsLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-white border-2 border-stone-100 rounded-2xl overflow-hidden animate-pulse"
                >
                  <div className="aspect-square bg-stone-100" />
                  <div className="p-2.5 space-y-2">
                    <div className="h-3 bg-stone-100 rounded w-3/4" />
                    <div className="h-3 bg-stone-100 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="p-5 bg-stone-100 rounded-2xl mb-4">
                <Package size={36} className="text-stone-300" />
              </div>
              <p className="text-stone-400 font-medium text-sm">
                {t("pos.noProducts")}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
              {filteredProducts.map((product, index) => {
                const inCart = cart.find((i) => i.id === product.id);
                const isLow =
                  product.stock_quantity > 0 &&
                  product.stock_quantity <= product.low_stock_threshold;
                const isOut = product.stock_quantity === 0;
                return (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, scale: 0.94 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: Math.min(index * 0.01, 0.3) }}
                    onClick={() => !isOut && addToCartDirect(product)}
                    className={`group relative bg-white border-2 rounded-2xl overflow-hidden cursor-pointer active:scale-[0.97] transition-all ${
                      isOut
                        ? "border-stone-100 opacity-40 cursor-not-allowed"
                        : inCart
                          ? "border-stone-800 shadow-lg shadow-stone-200/70"
                          : "border-stone-200 hover:border-stone-400 hover:shadow-md"
                    }`}
                  >
                    {inCart && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute top-2 left-2 z-10 bg-stone-800 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center shadow-md"
                      >
                        {inCart.quantity}
                      </motion.div>
                    )}
                    {isLow && (
                      <div className="absolute top-2 right-2 z-10 bg-amber-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow">
                        {t("pos.low")}
                      </div>
                    )}
                    <div className="aspect-square overflow-hidden bg-stone-50 relative">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${product.image_url}`}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {isOut && (
                        <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                          <span className="text-xs font-bold text-stone-500 bg-white px-3 py-1 rounded-full border border-stone-200">
                            {t("pos.outOfStock")}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-2.5">
                      <p className="font-semibold text-xs text-stone-800 line-clamp-2 min-h-[2rem] leading-snug">
                        {product.name}
                      </p>
                      <p className="text-[10px] text-stone-400 mt-0.5 truncate">
                        {product.category}
                      </p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="font-mono font-bold text-stone-800 text-sm">
                          ₹
                          {calculateDiscountedPrice(
                            product.selling_price,
                            product.discount,
                            product.discount_type,
                          )}
                        </span>
                        <div className="flex items-center gap-1">
                          {product.gst_rate != null && (
                            <span className="text-[9px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold">
                              {product.gst_rate}%
                            </span>
                          )}
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                              isOut
                                ? "bg-red-100 text-red-600"
                                : isLow
                                  ? "bg-amber-100 text-amber-600"
                                  : "bg-stone-100 text-stone-500"
                            }`}
                          >
                            {product.stock_quantity}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {!productsLoading && totalPages > 1 && (
          <div className="flex items-center justify-between px-4 md:px-6 py-3 bg-white border-t border-stone-100 shrink-0">
            <span className="text-xs text-stone-400">
              Page {currentPage} of {totalPages} · {totalProducts} products
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => fetchProducts(1)}
                disabled={currentPage === 1}
                className="px-2 py-1 text-xs rounded-lg bg-stone-100 text-stone-500 hover:bg-stone-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                «
              </button>
              <button
                onClick={() => fetchProducts(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-2.5 py-1 text-xs rounded-lg bg-stone-100 text-stone-500 hover:bg-stone-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                ‹
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const start = Math.max(
                  1,
                  Math.min(currentPage - 2, totalPages - 4),
                );
                const page = start + i;
                return (
                  <button
                    key={page}
                    onClick={() => fetchProducts(page)}
                    className={`w-7 h-7 text-xs rounded-lg font-semibold transition-colors ${page === currentPage ? "bg-stone-800 text-white" : "bg-stone-100 text-stone-500 hover:bg-stone-200"}`}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                onClick={() => fetchProducts(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-2.5 py-1 text-xs rounded-lg bg-stone-100 text-stone-500 hover:bg-stone-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                ›
              </button>
              <button
                onClick={() => fetchProducts(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2 py-1 text-xs rounded-lg bg-stone-100 text-stone-500 hover:bg-stone-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ═══════════ RIGHT: Cart + Billing Panel ═══════════ */}
      <div className="w-full bg-stone-900 flex flex-col text-white shadow-2xl max-h-[100vh] lg:w-[38%] border-l border-stone-800">
        {/* Cart header */}
        <div className="flex items-center gap-2 px-4 py-3.5 border-b border-stone-800 shrink-0">
          <ShoppingCart size={17} className="text-amber-400 shrink-0" />
          <h2 className="text-sm font-bold text-white">{t("pos.cart")}</h2>
          <span className="ml-auto text-xs font-mono text-stone-400 bg-stone-800 px-2.5 py-1 rounded-full">
            {cartItemCount} {t("pos.items")}
          </span>
          {cart.length > 0 && (
            <>
              <button
                onClick={holdCart}
                title={t("pos.holdOrder")}
                className="p-1.5 text-stone-500 hover:text-amber-400 hover:bg-stone-800 rounded-lg transition-colors"
              >
                <PauseCircle size={15} />
              </button>
              <button
                onClick={clearCart}
                title={t("pos.clearCart")}
                className="p-1.5 text-stone-500 hover:text-rose-400 hover:bg-stone-800 rounded-lg transition-colors"
              >
                <Trash2 size={15} />
              </button>
            </>
          )}
        </div>

        {/* Cart items */}
        <div className="flex-1 overflow-y-auto px-3 py-3 min-h-0">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[70vh] py-10 text-center gap-3">
              <div className="p-4 bg-stone-800/60 rounded-2xl">
                <ShoppingCart className="text-stone-600" size={32} />
              </div>
              <div>
                <p className="text-stone-500 font-medium text-sm">
                  {t("pos.cartEmpty")}
                </p>
                <p className="text-xs text-stone-600 mt-1">
                  {t("pos.addProducts")}
                </p>
              </div>
              <p className="text-[11px] text-stone-700 flex items-center gap-1.5 bg-stone-800/40 px-3 py-1.5 rounded-full">
                <Zap size={10} className="text-emerald-500" />
                {t("pos.scanHint")}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <AnimatePresence mode="popLayout">
                {cart.map((item) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20, height: 0 }}
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    className="flex items-center gap-2.5 p-2.5 bg-stone-800/80 rounded-xl border border-stone-700/60 hover:border-stone-600 transition-all"
                  >
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${item.image_url}`}
                      alt={item.name}
                      className="w-10 h-10 object-cover rounded-lg shrink-0 bg-stone-700"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-stone-200 truncate leading-tight">
                        {item.name}
                      </p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <p className="text-[10px] font-mono text-stone-500">
                          ₹
                          {calculateDiscountedPrice(
                            item.selling_price,
                            item.discount,
                            item.discount_type,
                          )}
                        </p>
                        {item.gst_rate != null && invoiceType === "gst" && (
                          <span className="text-[9px] font-bold bg-emerald-500/20 text-emerald-400 px-1 py-0.5 rounded">
                            {item.gst_rate}%
                          </span>
                        )}
                      </div>
                      {Number(item.discount || 0) > 0 && (
                        <div className="mt-1 space-y-0.5 text-[10px] leading-tight">
                          <p className="text-stone-500">
                            Original: ₹
                            {(
                              Number(item.selling_price || 0)
                            ).toFixed(2)}
                          </p>
                          <p className="font-semibold text-rose-400">
                            Discount{item.discount_type === "percentage" ? ` (${item.discount}%)` : ""} -₹
                            {(
                              Number(item.selling_price || 0) -
                              calculateDiscountedPrice(item.selling_price, item.discount, item.discount_type)
                            ).toFixed(2)} × {item.quantity} = -₹
                            {(
                              (Number(item.selling_price || 0) -
                                calculateDiscountedPrice(item.selling_price, item.discount, item.discount_type)) *
                              Number(item.quantity || 0)
                            ).toFixed(2)}
                          </p>
                        </div>
                      )}
                      <div className="flex items-center gap-1 mt-1.5">
                        <button
                          onClick={() => updateQuantity(item.id, -1)}
                          className="w-5 h-5 rounded-full bg-stone-700 hover:bg-stone-600 flex items-center justify-center transition-colors active:scale-95"
                        >
                          <Minus size={9} />
                        </button>
                        <input
                          type="number"
                          value={item.quantity}
                          min={1}
                          max={item.stock_quantity}
                          onChange={(e) =>
                            setQuantityDirect(item.id, e.target.value)
                          }
                          className="w-8 h-5 text-center text-xs font-mono font-bold bg-transparent text-stone-200 outline-none border-b border-stone-600"
                        />
                        <button
                          onClick={() => updateQuantity(item.id, 1)}
                          className="w-5 h-5 rounded-full bg-stone-700 hover:bg-amber-500 flex items-center justify-center transition-colors active:scale-95"
                        >
                          <Plus size={9} />
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => togglePurchased(item.id)}
                        className={`px-2 py-1 rounded-md text-[10px] font-black border ${item.purchased ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/40" : "bg-stone-700 text-stone-400 border-stone-600"}`}
                        title="P means Purchased, not Paid"
                      >
                        {item.purchased ? "P ✓" : "P"}
                      </button>
                      <span className="font-mono font-bold text-amber-400 text-sm">
                        ₹
                        {(
                          calculateDiscountedPrice(
                            item.selling_price,
                            item.discount,
                            item.discount_type,
                          ) * item.quantity
                        ).toFixed(2)}
                      </span>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="p-0.5 text-stone-600 hover:text-rose-400 transition-colors"
                      >
                        <X size={13} />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* ─── Billing Panel ─── */}
        <div
          className="border-t border-stone-800 px-3 pt-3 pb-4 space-y-3 shrink-0 bg-stone-900 overflow-y-auto"
          style={{ maxHeight: "calc(100vh - 220px)" }}
        >
          {/* Invoice type toggle */}
          <div className="flex items-center gap-1 bg-stone-800 p-1 rounded-xl">
            {/* <button
              onClick={() => setInvoiceType("non_gst")}
              className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${invoiceType === "non_gst" ? "bg-white text-stone-800 shadow" : "text-stone-500 hover:text-stone-200"}`}
            >
              <ReceiptText size={12} />
              {t("pos.nonGST")}
            </button> */}
            <button
              onClick={() => setInvoiceType("gst")}
              className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${invoiceType === "gst" ? "bg-emerald-500 text-white shadow" : "text-stone-500 hover:text-stone-200"}`}
            >
              <FileText size={12} />
              {t("pos.gstInvoice")}
            </button>
          </div>

          {/* GST settings */}
          {invoiceType === "gst" && (
            <div className="bg-stone-800 rounded-xl p-3 space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wide">
                  {t("pos.gstType")}
                </span>
                <div className="flex gap-1">
                  {["cgst_sgst", "igst"].map((gt) => (
                    <button
                      key={gt}
                      onClick={() => setGstType(gt)}
                      className={`px-2.5 py-1 text-[10px] font-bold rounded-lg transition-all ${gstType === gt ? "bg-emerald-500 text-white" : "bg-stone-700 text-stone-400 hover:bg-stone-600"}`}
                    >
                      {gt === "cgst_sgst" ? "CGST+SGST" : "IGST"}
                    </button>
                  ))}
                </div>
              </div>
              {Object.keys(gstBreakdown).length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1 border-t border-stone-700">
                  {Object.entries(gstBreakdown)
                    .sort((a, b) => Number(a[0]) - Number(b[0]))
                    .map(([rate, amt]) => (
                      <span
                        key={rate}
                        className="text-[10px] font-bold bg-emerald-500/15 border border-emerald-500/25 text-emerald-400 px-2 py-0.5 rounded-full"
                      >
                        {rate}% → ₹{amt.toFixed(2)}
                      </span>
                    ))}
                </div>
              )}
              <p className="text-[10px] text-stone-600 flex items-center gap-1">
                <Tag size={9} className="text-emerald-500 shrink-0" />
                Rates per product from Category GST settings
              </p>
            </div>
          )}

          {/* Discount */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-stone-400 font-medium shrink-0">
              {t("pos.discount")}
            </span>
            <button
              onClick={() =>
                setDiscountMode((m) => (m === "percent" ? "flat" : "percent"))
              }
              className="flex items-center gap-1 text-[10px] font-bold text-stone-400 bg-stone-800 border border-stone-700 px-2 py-1.5 rounded-lg hover:border-stone-500 transition-colors shrink-0"
            >
              {discountMode === "percent" ? (
                <>
                  <Percent size={10} />
                </>
              ) : (
                <>
                  <IndianRupee size={10} />
                </>
              )}
            </button>
            <div className="relative flex-1">
              <input
                type="text"
                inputMode="decimal"
                min={0}
                value={discountVal}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "" || /^\d*\.?\d*$/.test(v)) setDiscountVal(v);
                }}
                className="w-full h-8 bg-stone-800 border border-stone-700 rounded-lg px-3 pr-7 text-sm font-mono text-stone-200 outline-none focus:border-amber-500 transition-colors"
              />
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-500 text-xs">
                {discountMode === "percent" ? "%" : "₹"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-stone-400 font-medium shrink-0 flex items-center gap-1">
              <Truck size={11} className="text-stone-500" />
              Transport
            </span>
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 text-xs font-mono">
                ₹
              </span>
              <input
                type="text"
                inputMode="decimal"
                min={0}
                value={transportCharges}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "" || /^\d*\.?\d*$/.test(v)) setTransportCharges(v);
                }}
                placeholder="0.00"
                className="w-full h-8 bg-stone-800 border border-stone-700 rounded-lg pl-6 pr-3 text-sm font-mono text-stone-200 outline-none focus:border-amber-500 placeholder-stone-600 transition-colors"
              />
            </div>
          </div>
          {/* Payment mode — 3+2 responsive grid */}
          <div>
            <p className="text-[10px] font-bold text-stone-400 uppercase tracking-wide mb-2 flex items-center justify-between">
              <span>
                {t("pos.paymentMode")} <span className="text-rose-400 font-black">*</span>
              </span>
              {!paymentMode && (
                <span className="text-[9px] font-bold text-amber-400 animate-pulse">
                  Required
                </span>
              )}
            </p>

            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {PAYMENT_MODES.map((mode) => (
                <PayBtn
                  key={mode}
                  mode={mode}
                  active={paymentMode === mode}
                  onClick={(newMode) => {
                    setPaymentMode(newMode);
                    if (['cash', 'card', 'upi'].includes(newMode)) {
                      setCreditBreakdown({ cash: '', card: '', upi: '', [newMode]: String(total) });
                    }
                  }}
                  t={t}
                />
              ))}
            </div>

            {paymentMode === "bank_transfer" && (
              <div className="mt-2.5 bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-3 space-y-2.5">
                <p className="text-[10px] font-bold text-cyan-400 uppercase tracking-wide">
                  Bank Transfer Details
                </p>
                {[
                  ["accountName", "Account holder name"],
                  ["accountNumber", "Account number"],
                  ["ifscCode", "IFSC code"],
                ].map(([key, placeholder]) => (
                  <input
                    key={key}
                    type="text"
                    value={bankTransfer[key]}
                    onChange={(e) =>
                      setBankTransfer((prev) => ({
                        ...prev,
                        [key]: e.target.value,
                      }))
                    }
                    placeholder={placeholder}
                    className="w-full h-8 bg-stone-800 border border-stone-700 rounded-lg px-3 text-xs text-white outline-none focus:border-cyan-500 placeholder-stone-600"
                  />
                ))}
                <input
                  type="text"
                  inputMode="decimal"
                  value={bankTransfer.amount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || /^\d*\.?\d*$/.test(value))
                      setBankTransfer((prev) => ({ ...prev, amount: value }));
                  }}
                  placeholder="Amount received now"
                  className="w-full h-8 bg-stone-800 border border-stone-700 rounded-lg px-3 text-xs text-white outline-none focus:border-cyan-500 placeholder-stone-600"
                />
              </div>
            )}

            {/* Payment received / balance panel */}
            {(paymentMode !== "credit" || paymentMode === "credit") && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-2.5 overflow-hidden"
              >
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 space-y-2.5">
                  <div className="flex items-center gap-2">
                    <BookOpen size={12} className="text-amber-400 shrink-0" />
                    <p className="text-[10px] font-bold text-amber-400 uppercase tracking-wide">
                      {paymentMode === "bank_transfer"
                        ? "Bank Transfer"
                        : `${paymentMode.toUpperCase()} Payment`}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-[9px] text-stone-500 uppercase tracking-wider font-bold">
                      Amount received now:
                    </p>
                    {[
                      { key: "cash", label: "Cash", icon: "💵" },
                      { key: "card", label: "Card", icon: "💳" },
                      { key: "upi", label: "UPI", icon: "📲" },
                    ].map(({ key, label, icon }) => (
                      <div key={key} className="flex items-center gap-2">
                        <span className="text-sm w-5 text-center shrink-0">
                          {icon}
                        </span>
                        <span className="text-[10px] text-stone-400 font-semibold w-12 shrink-0">
                          {label}
                        </span>
                        <div className="relative flex-1">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-500 text-xs font-mono">
                            ₹
                          </span>
                          <input
                            type="text"
                            inputMode="decimal"
                            min={0}
                            value={creditBreakdown[key]}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === "" || /^\d*\.?\d*$/.test(v)) {
                                setCreditBreakdown((prev) => ({
                                  ...prev,
                                  [key]: v,
                                }));
                              }
                            }}
                            placeholder="0.00"
                            className="w-full h-8 bg-stone-800 border border-stone-700 rounded-lg pl-6 pr-3 text-sm font-mono text-white outline-none focus:border-amber-500 placeholder-stone-600 transition-colors"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="bg-stone-800/60 rounded-lg px-3 py-2 text-center border border-rose-500/20">
                    <p className="text-[9px] text-stone-500 font-bold uppercase">
                      Balance due on delivery
                    </p>
                    <p className="font-mono font-black text-base text-rose-400">
                      ₹
                      {Math.max(
                        0,
                        Math.round(
                          (afterDiscount - (amountReceived || 0)) * 100,
                        ) / 100,
                      ).toFixed(2)}
                    </p>
                  </div>
                  {!customerName.trim() &&
                    Math.max(0, total - (amountReceived || 0)) > 0.01 && (
                      <p className="text-[10px] text-amber-400 flex items-center gap-1">
                        <AlertTriangle size={10} />
                        Customer name required for credit sales
                      </p>
                    )}
                </div>
              </motion.div>
            )}
          </div>

          {/* Customer (collapsible) */}
          <div className="bg-stone-800 rounded-xl overflow-hidden border border-stone-700/50">
            <button
              onClick={() => setShowCustomer((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-2.5 text-xs font-semibold text-stone-300 hover:text-white transition-colors"
            >
              <span className="flex items-center gap-2">
                <User
                  size={13}
                  className={customerName ? "text-amber-400" : "text-stone-500"}
                />
                {customerName || t("pos.customerDetails")}
                {customerName && (
                  <span className="text-[9px] text-amber-400 font-normal">
                    {customerPhone}
                  </span>
                )}
              </span>
              {showCustomer ? (
                <ChevronUp size={13} />
              ) : (
                <ChevronDown size={13} />
              )}
            </button>
            {showCustomer && (
              <div className="px-3 pb-3 space-y-2 border-t border-stone-700">
                <div className="relative mt-2">
                  <input
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder={t("pos.customerPhone")}
                    type="tel"
                    required
                    className="w-full h-8 bg-stone-700 border border-stone-600 rounded-lg px-3 text-xs text-stone-200 outline-none focus:border-amber-500 placeholder-stone-500 transition-colors"
                  />
                  {lookingUpCustomer && (
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-amber-400 font-semibold animate-pulse">
                      Checking...
                    </span>
                  )}
                </div>

                {returningCustomerInfo && (
                  <div className="flex items-center justify-between px-2.5 py-1.5 bg-emerald-950/60 border border-emerald-500/40 rounded-lg text-[10px] text-emerald-300">
                    <span className="font-semibold flex items-center gap-1">
                      <span>✓</span> Returning Customer ({returningCustomerInfo.total_orders} past orders)
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        if (returningCustomerInfo.customer_name) setCustomerName(returningCustomerInfo.customer_name);
                        if (returningCustomerInfo.customer_address) setCustomerAddress(returningCustomerInfo.customer_address);
                        if (returningCustomerInfo.customer_gstin) setCustomerGSTIN(returningCustomerInfo.customer_gstin);
                      }}
                      className="text-[9px] underline text-emerald-400 hover:text-emerald-200"
                    >
                      Refill
                    </button>
                  </div>
                )}

                <input
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder={t("pos.customerName")}
                  className="w-full h-8 bg-stone-700 border border-stone-600 rounded-lg px-3 text-xs text-stone-200 outline-none focus:border-amber-500 placeholder-stone-500 transition-colors"
                />
                <textarea
                  value={customerAddress}
                  onChange={(e) => setCustomerAddress(e.target.value)}
                  placeholder="Billing / Shipping Address (optional)"
                  rows={2}
                  className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-xs text-stone-200 outline-none focus:border-amber-500 placeholder-stone-500 resize-none transition-colors"
                />
                {invoiceType === "gst" && (
                  <input
                    value={customerGSTIN}
                    onChange={(e) =>
                      setCustomerGSTIN(e.target.value.toUpperCase())
                    }
                    placeholder={t("pos.customerGSTIN")}
                    className="w-full h-8 bg-stone-700 border border-stone-600 rounded-lg px-3 text-xs font-mono text-stone-200 outline-none focus:border-emerald-500 placeholder-stone-500 transition-colors"
                  />
                )}
                <input
                  value={narration}
                  onChange={(e) => setNarration(e.target.value)}
                  placeholder={t("pos.narration")}
                  className="w-full h-8 bg-stone-700 border border-stone-600 rounded-lg px-3 text-xs text-stone-200 outline-none focus:border-amber-500 placeholder-stone-500 transition-colors"
                />
                <div>
                  <p className="text-[9px] text-stone-500 font-bold uppercase tracking-wider mb-1 flex items-center gap-1">
                    <span>🏆</span> Referred by Staff (for commission)
                  </p>
                  <select
                    value={referredByStaffId}
                    onChange={(e) => setReferredByStaffId(e.target.value)}
                    className="w-full h-8 bg-stone-700 border border-stone-600 rounded-lg px-2 text-xs text-stone-200 outline-none focus:border-amber-500 transition-colors"
                  >
                    <option value="">— No referral —</option>
                    {staffList.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({s.role})
                      </option>
                    ))}
                  </select>
                  {referredByStaffId && (
                    <p className="text-[9px] text-amber-400 mt-1 flex items-center gap-1">
                      ✓ Commission will be credited to{" "}
                      {staffList.find((s) => s.id === referredByStaffId)?.name}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={enableScheduledDelivery}
                onChange={(e) => setEnableScheduledDelivery(e.target.checked)}
                className="w-4 h-4 rounded border-stone-700 bg-stone-800 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-0"
              />
              <div className="flex items-center gap-2 text-sm font-medium text-stone-300">
                <Truck className="w-4 h-4" />
                Schedule Delivery
              </div>
            </label>
            {/* Google Review — mandatory before checkout */}
            <div className="bg-stone-800 rounded-xl p-3 space-y-2 border border-stone-700/50">
              <label className="text-[10px] font-bold text-stone-400 uppercase tracking-wide flex items-center gap-1.5">
                <Star size={12} className="text-amber-400" />
                Google Review <span className="text-rose-400">*</span>
              </label>
              <textarea
                value={googleReviewText}
                onChange={(e) => setGoogleReviewText(e.target.value)}
                placeholder="Paste the customer's Google review or review link here"
                rows={2}
                className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-xs text-stone-200 outline-none focus:border-amber-500 placeholder-stone-500 resize-none transition-colors"
              />
              {!googleReviewText.trim() && (
                <p className="text-[10px] text-amber-400 flex items-center gap-1">
                  <AlertTriangle size={10} />
                  Required before checkout
                </p>
              )}
            </div>
            {enableScheduledDelivery && (
              <div className="space-y-2 pl-6">
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  min={todayStr()}
                  className="w-full px-3 py-2 bg-stone-800 border border-stone-700 rounded-lg text-stone-100 focus:outline-none focus:border-emerald-500 text-sm"
                />
                {isPartialPayment && (
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-2 text-xs text-blue-400">
                    <div className="font-medium mb-1">
                      Partial Payment Delivery
                    </div>
                    <div>Customer will pay {rupee(balanceDue)} on delivery</div>
                  </div>
                )}
                {isFullPayment && (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2 text-xs text-emerald-400">
                    <div className="font-medium mb-1">Fully Paid Delivery</div>
                    <div>Customer will pick up goods on scheduled date</div>
                  </div>
                )}
              </div>
            )}
          </div>
          {safeTransport > 0 && (
            <div className="flex justify-between text-xs text-stone-400">
              <span className="flex items-center gap-1">
                <Truck size={10} /> Transport
              </span>
              <span className="font-mono text-stone-300">
                +₹{safeTransport.toFixed(2)}
              </span>
            </div>
          )}
          {/* Totals */}
          <div className="bg-stone-800 rounded-xl px-3 py-3 space-y-1.5 border border-stone-700/50">
            <div className="flex justify-between text-xs text-stone-400">
              <span>Amount before discount</span>
              <span className="font-mono text-stone-300">
                ₹{amountBeforeDiscount.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between text-xs text-stone-400">
              <span>After item discounts</span>
              <span className="font-mono text-stone-300">
                ₹{subtotal.toFixed(2)}
              </span>
            </div>
            {discountAmount > 0 && (
              <div className="flex justify-between text-xs text-stone-400">
                <span>
                  {t("pos.discount")}
                  {discountMode === "percent" ? ` (${discountVal}%)` : ""}
                </span>
                <span className="font-mono text-emerald-400">
                  -₹{discountAmount.toFixed(2)}
                </span>
              </div>
            )}
            {itemDiscountTotal > 0 && (
              <div className="flex items-center justify-between text-xs text-rose-400">
                <span>Item discounts</span>
                <span>-₹{itemDiscountTotal.toFixed(2)}</span>
              </div>
            )}
            {totalDiscount > 0 && (
              <div className="flex justify-between text-xs font-semibold text-rose-300 border-t border-stone-700 pt-1.5">
                <span>Total discount</span>
                <span className="font-mono">-₹{totalDiscount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-xs font-semibold text-emerald-400">
              <span>Amount after discount</span>
              <span className="font-mono">₹{afterDiscount.toFixed(2)}</span>
            </div>
            {invoiceType === "gst" &&
              Object.entries(gstBreakdown)
                .sort((a, b) => Number(a[0]) - Number(b[0]))
                .map(([rate, amt]) =>
                  gstType === "cgst_sgst" ? (
                    <React.Fragment key={rate}>
                      <div className="flex justify-between text-[10px] text-stone-500">
                        <span>
                          CGST {Number(rate) / 2}%{" "}
                          <span className="text-stone-600">(incl.)</span>
                        </span>
                        <span className="font-mono">
                          ₹{(amt / 2).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-[10px] text-stone-500">
                        <span>SGST {Number(rate) / 2}%</span>
                        <span className="font-mono">
                          ₹{(amt / 2).toFixed(2)}
                        </span>
                      </div>
                    </React.Fragment>
                  ) : (
                    <div
                      key={rate}
                      className="flex justify-between text-[10px] text-stone-500"
                    >
                      <span>
                        IGST {rate}%{" "}
                        <span className="text-stone-600">(incl.)</span>
                      </span>
                      <span className="font-mono">₹{amt.toFixed(2)}</span>
                    </div>
                  ),
                )}
            {Math.abs(roundOff) >= 0.01 && (
              <div className="flex justify-between text-[10px] text-stone-500">
                <span>{t("pos.roundOff")}</span>
                <span className="font-mono">
                  {roundOff > 0 ? "+" : ""}₹{roundOff.toFixed(2)}
                </span>
              </div>
            )}
            <div className="flex justify-between font-bold border-t border-stone-700 pt-2 mt-1">
              <span className="text-sm text-white">{t("pos.total")}</span>
              <span className="font-mono text-xl text-amber-400">
                ₹{total.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Checkout button */}
          {lastOrder && (
            <button
              onClick={() => setShowPrintDialog(true)}
              className="w-full h-9 rounded-xl border border-stone-700 text-stone-400 hover:border-stone-500 hover:text-stone-200 transition-all flex items-center justify-center gap-2 text-xs font-medium"
            >
              <Printer size={14} />
              {t("pos.printInvoice")} — #{lastOrder.invoice_number}
            </button>
          )}
          <button
            onClick={handleCheckout}
            disabled={isCheckoutDisabled()}
            className="w-full h-12 rounded-2xl bg-amber-500 hover:bg-amber-400 text-stone-900 font-bold text-base transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-amber-900/30 active:scale-[0.98]"
          >
            {checkingOut ? (
              <>
                <svg
                  className="animate-spin h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                {t("pos.pleaseWait")}
              </>
            ) : (
              <>
                <ReceiptText size={18} />
                {t("pos.checkout")} — ₹{total.toFixed(2)}
              </>
            )}
          </button>
          {isCheckoutDisabled() && cart.length > 0 && (
            <p className="text-[11px] text-amber-400 font-medium text-center flex items-center justify-center gap-1.5 -mt-1">
              <AlertTriangle size={12} className="text-amber-400 shrink-0" />
              {!paymentMode
                ? "Select a Payment Mode (Cash, Card, UPI, etc.) to checkout"
                : !googleReviewText.trim()
                  ? "Customer Google review required before checkout"
                  : paymentMode === "split" && !splitValid
                    ? "Split amounts must equal total"
                    : "Complete required details to checkout"}
            </p>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════
            IMPROVED CAMERA SCANNER DIALOG — mobile-first
          ══════════════════════════════════════════════════════════ */}
      <Dialog
        open={showScanner}
        onOpenChange={(open) => {
          setShowScanner(open);
          if (!open) {
            setScannerError(null);
            setManualBarcodeInput("");
          }
        }}
      >
        <DialogContent className="max-w-sm w-full mx-auto p-0 gap-0 overflow-hidden rounded-2xl">
          {/* Dialog header */}
          <div className="flex items-center justify-between px-4 py-3 bg-stone-900 text-white">
            <div className="flex items-center gap-2">
              <Scan size={17} className="text-amber-400" />
              <span className="font-bold text-sm">{t("pos.scanBarcode")}</span>
            </div>
            {/* Mode toggle: camera / manual */}
            <div className="flex items-center gap-1 bg-stone-800 rounded-lg p-0.5">
              <button
                onClick={() => {
                  setScannerMode("camera");
                  setScannerError(null);
                }}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${scannerMode === "camera" ? "bg-stone-600 text-white" : "text-stone-400 hover:text-stone-200"}`}
              >
                <Camera size={12} /> Camera
              </button>
              <button
                onClick={() => {
                  setScannerMode("manual");
                  setTimeout(() => manualInputRef.current?.focus(), 100);
                }}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${scannerMode === "manual" ? "bg-stone-600 text-white" : "text-stone-400 hover:text-stone-200"}`}
              >
                <Keyboard size={12} /> Manual
              </button>
            </div>
          </div>

          {scannerMode === "camera" ? (
            <div className="bg-black">
              {/* Camera flip button */}
              <div className="flex items-center justify-between px-4 py-2 bg-stone-900/80">
                <p className="text-[11px] text-stone-400">
                  {t("pos.scanHint")}
                </p>
                <button
                  onClick={() =>
                    setScannerFacing((f) =>
                      f === "environment" ? "user" : "environment",
                    )
                  }
                  className="flex items-center gap-1.5 text-[11px] text-stone-400 hover:text-white bg-stone-800 px-2.5 py-1.5 rounded-lg transition-colors"
                >
                  <FlipHorizontal size={12} />
                  {scannerFacing === "environment" ? "Rear" : "Front"}
                </button>
              </div>

              {/* Scanner viewport */}
              {scannerError ? (
                /* Error state with fallback */
                <div className="flex flex-col items-center justify-center py-10 px-6 gap-4 bg-stone-900 text-center">
                  <div className="p-4 bg-rose-500/10 rounded-2xl border border-rose-500/20">
                    <WifiOff size={32} className="text-rose-400" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm mb-1">
                      Camera unavailable
                    </p>
                    <p className="text-stone-400 text-xs leading-relaxed">
                      {String(scannerError).includes("Permission") ||
                      String(scannerError).includes("NotAllowed")
                        ? "Camera permission denied. Please allow camera access in your browser settings."
                        : "Could not access camera. Please check permissions or use manual entry below."}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setScannerMode("manual");
                      setScannerError(null);
                      setTimeout(() => manualInputRef.current?.focus(), 100);
                    }}
                    className="flex items-center gap-2 bg-amber-500 text-stone-900 font-bold text-sm px-4 py-2.5 rounded-xl hover:bg-amber-400 transition-colors"
                  >
                    <Keyboard size={15} />
                    Switch to Manual Entry
                  </button>
                </div>
              ) : (
                <div className="relative" style={{ aspectRatio: "1/1" }}>
                  {/* Scan guide overlay */}
                  <div className="absolute inset-0 z-10 pointer-events-none flex items-center justify-center">
                    <div className="w-48 h-48 relative">
                      {/* Corner brackets */}
                      {[
                        [
                          "top-0 left-0 border-t-2 border-l-2 rounded-tl-lg",
                          "",
                        ],
                        [
                          "top-0 right-0 border-t-2 border-r-2 rounded-tr-lg",
                          "",
                        ],
                        [
                          "bottom-0 left-0 border-b-2 border-l-2 rounded-bl-lg",
                          "",
                        ],
                        [
                          "bottom-0 right-0 border-b-2 border-r-2 rounded-br-lg",
                          "",
                        ],
                      ].map(([cls], i) => (
                        <div
                          key={i}
                          className={`absolute w-8 h-8 border-amber-400 ${cls}`}
                        />
                      ))}
                      {/* Scan line animation */}
                      <div
                        className="absolute inset-x-0 top-0 h-0.5 bg-amber-400/70 animate-bounce"
                        style={{ animationDuration: "1.5s" }}
                      />
                    </div>
                  </div>
                  <Scanner
                    onScan={handleCameraScan}
                    onError={(err) => {
                      console.error("Scanner error:", err);
                      setScannerError(err);
                    }}
                    constraints={{
                      video: {
                        facingMode: { ideal: scannerFacing },
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                      },
                    }}
                    formats={[
                      "qr_code",
                      "code_128",
                      "code_39",
                      "ean_13",
                      "ean_8",
                      "upc_a",
                      "upc_e",
                    ]}
                    components={{ audio: false }}
                    styles={{ container: { width: "100%", height: "100%" } }}
                  />
                </div>
              )}

              {/* Bottom hint */}
              {!scannerError && (
                <div className="px-4 py-2.5 bg-stone-900/90 flex items-center justify-between">
                  <p className="text-[11px] text-stone-500">
                    Point camera at barcode
                  </p>
                  <button
                    onClick={() => {
                      setScannerMode("manual");
                      setTimeout(() => manualInputRef.current?.focus(), 100);
                    }}
                    className="text-[11px] text-amber-400 font-semibold flex items-center gap-1 hover:text-amber-300 transition-colors"
                  >
                    <Keyboard size={11} />
                    Type instead
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* Manual barcode entry */
            <div className="p-5 bg-white space-y-4">
              <div className="text-center">
                <div className="w-14 h-14 bg-stone-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Keyboard size={26} className="text-stone-500" />
                </div>
                <p className="font-semibold text-stone-800 text-sm">
                  Enter Barcode Manually
                </p>
                <p className="text-xs text-stone-400 mt-1">
                  Type or paste the barcode number below
                </p>
              </div>
              <div className="space-y-3">
                <input
                  ref={manualInputRef}
                  type="text"
                  inputMode="text"
                  value={manualBarcodeInput}
                  onChange={(e) => setManualBarcodeInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleManualBarcodeSubmit();
                  }}
                  placeholder="e.g. 8901234567890"
                  autoFocus
                  className="w-full h-11 border-2 border-stone-200 rounded-xl px-4 text-base font-mono text-stone-800 outline-none focus:border-amber-400 transition-colors text-center tracking-widest placeholder-stone-300"
                />
                <button
                  onClick={handleManualBarcodeSubmit}
                  disabled={!manualBarcodeInput.trim()}
                  className="w-full h-11 bg-stone-800 hover:bg-stone-700 text-white font-bold text-sm rounded-xl disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 active:scale-[0.98]"
                >
                  <Search size={15} />
                  Search Product
                </button>
              </div>
              <button
                onClick={() => {
                  setScannerMode("camera");
                  setScannerError(null);
                }}
                className="w-full text-xs text-stone-400 hover:text-stone-600 flex items-center justify-center gap-1.5 transition-colors"
              >
                <Camera size={12} />
                Try camera instead
              </button>
            </div>
          )}

          {/* Close button */}
          <div className="px-4 py-3 bg-stone-50 border-t border-stone-100">
            <Button
              variant="outline"
              onClick={() => {
                setShowScanner(false);
                setScannerError(null);
                setManualBarcodeInput("");
              }}
              className="w-full rounded-xl text-sm font-semibold h-10"
            >
              {t("common.cancel")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Print Preview Dialog ── */}
      <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
        <DialogContent className="max-w-sm max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer size={18} />
              {t("pos.invoicePreview")}
            </DialogTitle>
          </DialogHeader>
          {lastOrder && (
            <div className="border border-stone-200 rounded-xl overflow-hidden bg-white text-xs font-mono p-5 space-y-2">
              <div className="text-center border-b border-dashed pb-3 mb-3">
                <p className="text-lg font-black tracking-widest">
                  MRB HOME DECOR
                </p>
                <p className="text-[10px] text-stone-500">Home Decor Mall</p>
                <p className="text-xs font-bold mt-1">
                  {lastOrder.gst_type ? "TAX INVOICE" : "RECEIPT (Non-Tax)"}
                </p>
              </div>
              <div className="space-y-1 text-[11px]">
                {[
                  ["Invoice#", lastOrder.invoice_number],
                  [
                    "Date",
                    new Date(lastOrder.created_at).toLocaleDateString("en-IN"),
                  ],
                  ["Cashier", user?.name || "Staff"],
                  ...(lastOrder.customer_name
                    ? [["Bill To", lastOrder.customer_name]]
                    : []),
                  ...(lastOrder.customer_phone
                    ? [["Phone", lastOrder.customer_phone]]
                    : []),
                  ...(lastOrder.customer_gstin
                    ? [["GSTIN", lastOrder.customer_gstin]]
                    : []),
                  ["Payment", (lastOrder.payment_mode || "cash").toUpperCase()],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <b>{k}</b>
                    <span>{v}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-b border-dashed py-2 my-2 space-y-1">
                {lastOrder.items.map((item, i) => (
                  <div key={i} className="flex justify-between text-[11px]">
                    <span className="flex-1 pr-2">
                      {item.product_name} × {item.quantity}
                      {item.gst_rate ? (
                        <span className="text-stone-400 ml-1">
                          (GST {item.gst_rate}%)
                        </span>
                      ) : null}
                    </span>
                    <span className="font-bold">₹{item.total.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-1 text-[11px]">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>₹{lastOrder.subtotal.toFixed(2)}</span>
                </div>
                {lastOrder.tax > 0 && (
                  <div className="flex justify-between">
                    <span>Tax</span>
                    <span>+₹{lastOrder.tax.toFixed(2)}</span>
                  </div>
                )}
                {lastOrder.discount > 0 && (
                  <div className="flex justify-between">
                    <span>Discount</span>
                    <span className="text-emerald-600">
                      -₹{lastOrder.discount.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between font-black border-t border-stone-300 pt-1.5 mt-1.5 text-sm">
                  <span>TOTAL</span>
                  <span>₹{lastOrder.total.toFixed(2)}</span>
                </div>
              </div>
              <p className="text-center text-[10px] text-stone-400 border-t border-dashed pt-2 mt-2">
                Thank you for your purchase!
              </p>
            </div>
          )}
          <div className="flex gap-3 mt-2">
            <Button
              variant="outline"
              onClick={() => setShowPrintDialog(false)}
              className="flex-1 rounded-xl"
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => {
                setShowPrintDialog(false);
                setTimeout(() => printInvoice(lastOrder, user?.name), 100);
              }}
              className="flex-1 rounded-xl gap-2 bg-stone-800"
            >
              <Printer size={15} />
              {t("pos.print")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Held Orders Panel ── */}
      <Dialog open={showHoldPanel} onOpenChange={setShowHoldPanel}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PauseCircle size={18} />
              {t("pos.heldOrders")}
            </DialogTitle>
          </DialogHeader>
          {heldOrders.length === 0 ? (
            <div className="text-center py-8 text-stone-400 text-sm">
              {t("pos.noHeldOrders")}
            </div>
          ) : (
            <div className="space-y-2">
              {heldOrders.map((held) => (
                <div
                  key={held.id}
                  className="flex items-center justify-between p-3 bg-stone-50 rounded-xl border border-stone-200"
                >
                  <div>
                    <p className="text-sm font-semibold text-stone-800">
                      {held.label}
                    </p>
                    <p className="text-xs text-stone-400">
                      {held.cart.reduce((s, i) => s + i.quantity, 0)}{" "}
                      {t("pos.items")} · ₹
                      {held.cart
                        .reduce(
                          (s, i) =>
                            s +
                            calculateDiscountedPrice(
                              i.selling_price,
                              i.discount,
                              i.discount_type,
                            ) *
                              i.quantity,
                          0,
                        )
                        .toFixed(2)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() =>
                        setHeldOrders((prev) =>
                          prev.filter((h) => h.id !== held.id),
                        )
                      }
                      className="p-1.5 text-stone-400 hover:text-rose-500 rounded-lg hover:bg-rose-50 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                    <button
                      onClick={() => resumeHeld(held)}
                      className="flex items-center gap-1.5 text-xs font-semibold bg-stone-800 text-white px-3 py-1.5 rounded-lg hover:bg-stone-700 transition-colors"
                    >
                      <PlayCircle size={13} />
                      {t("pos.resume")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <AnimatePresence>
        {showPendingOrders && (
          <>
            <div
              onClick={() => setShowPendingOrders(false)}
              className="fixed inset-0 bg-black/30 z-40"
            />
            <PendingOrdersPanel
              onSelectOrder={loadTemporaryOrder}
              onClose={() => setShowPendingOrders(false)}
            />
          </>
        )}
        {showCustomerOrders && (
          <>
            <div onClick={() => setShowCustomerOrders(false)} className="fixed inset-0 bg-black/30 z-40" />
            <CustomerOrdersPanel onClose={() => setShowCustomerOrders(false)} />
          </>
        )}
        {showPurchaseOrders && (
          <>
            <div onClick={() => setShowPurchaseOrders(false)} className="fixed inset-0 bg-black/30 z-40" />
            <PurchaseOrdersPanel onClose={() => setShowPurchaseOrders(false)} />
          </>
        )}
      </AnimatePresence>
      <GodownApprovalsModal isOpen={showGodownApprovals} onClose={() => setShowGodownApprovals(false)} />
    </div>
  );
};

export default POS;
